import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { RelatedDocumentsTab } from '@ui-nuvertos/shared/related-documents-tab/related-documents-tab';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { lastValueFrom, Subject } from 'rxjs';
import moment from 'moment';

import { InputTextModule } from "primeng/inputtext";
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DatePickerModule } from 'primeng/datepicker';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { DynamicForm } from '@ui-nuvertos/shared/dynamic-form/dynamic-form';
import { TabsModule } from 'primeng/tabs';
import { CheckboxModule } from 'primeng/checkbox';
import { ItemDynamicForm } from '../item-dynamic-form/item-dynamic-form';
import { TemplateService } from '@ui-nuvertos/shared/services/template/template.service';
import { ItemService } from '../services/item/item.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ClientOrderService } from '../services/client/client-order.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { ClientOnboardingService } from '../services/client/client-onboarding.service';
import { ClientListService } from '../services/client/client-list.service';
import { MastersService } from '../services/masters/masters.service';
import { TableModule } from 'primeng/table';
import { ClientTypeaheadUtils } from '../utils/client-typeahead.utils';
import { PayloadGeneratorService } from '../services/payload/payload-generator.service';
import { PayloadValidationError } from '../services/payload/payload-generator.service';
import { ClientOrderCreatePayload, ClientOrderUpdatePayload, CLIENT_ORDER_FIELD_TO_INTERFACE_MAP, CLIENT_ORDER_TABLE_TO_INTERFACE_MAP } from '../interfaces/client-order-payload.interface';
import { S3FileUploadService, S3UploadResult } from '../services/s3/s3-file-upload.service';
import { FiscalPeriodUtils } from '../utils/fiscal-period.utils';
import { PageActionService } from '../services/page-action/page-action.service';
import { PdfApiService } from '../services/pdf-api.service';

interface FieldConfig {
  id: number;
  template_id: number;
  field_name: string;
  display_name: string;
  field_type: string;
  data_type: string;
  is_mandatory: boolean;
  is_visible: boolean;
  is_custom_field: boolean;
  tab_id: string | number;
  sub_tab_id: number | string;
  sub_tab_key?: string;
  display_order: number;
  validation_rules: string;
  help_text: string;
  default_value: string;
  is_active: boolean;
  options: any[];
  is_deleted: boolean;
  created_by: number;
  updated_by: number;
  created_at: string;
  updated_at: string;
}

/**
 * Discriminated union for file upload events
 */
type UploadEvent =
  | { mode: 'table'; file: File; field: any; tableKey: string; rowIndex: number }
  | { mode: 'form'; file: File; field: any };

@Component({
  selector: 'lib-client-so',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    AutoCompleteModule,
    DatePickerModule,
    RadioButtonModule,
    SelectModule,
    ButtonModule,
    DynamicForm,
    TabsModule,
    CheckboxModule,
    ItemDynamicForm,
    TableModule,
    RelatedDocumentsTab,
  ],
  templateUrl: './client-so.html',
  styleUrl: './client-so.css',
})
export class ClientSo {

  isLoading = false;
  customerForm!: FormGroup;
  primaryInfoExpanded = true;
  data: FieldConfig[] = [];
  dynamicFormTabs: { title: string; id: number; is_active: boolean; sub_tabs: any }[] = [];
  primaryFields: FieldConfig[] | null = null;
  templates: any;
  masterFields: any;
  customFields: any;
  viewMode: boolean = true;
  mode: 'create' | 'edit' | 'view' | 'duplicate' = 'create';
  clientSOId: any = null;

  // Related Documents tab (view mode only)
  readonly RELATED_DOCS_TAB_VALUE = '__related_documents__';
  approvalStatus: string = 'not_required'; // 'pending' | 'approved' | 'rejected' | 'not_required'
  soStatus: string = 'open'; // 'open' | 'partially_complete' | 'partially_billed' | 'billed' | 'closed'
  isCancelled: boolean = false; // Track if SO is cancelled
  soData: any = null; // Store SO data for close operation
  private destroy$ = new Subject<void>();

  defaultTemplate: any;
  private tableFormArrays: Map<string, FormArray> = new Map();
  private searchSuggestionsMap: Map<string, any[]> = new Map();
  clientOptions: any[] = [];
  templateOptions: any[] = [];
  currentTemplateId: string | null = null;
  unitsOptions: any[] = [];
  masterData: any = {
    'tax-types': [],
    'tax-rates': [],
    'registration-type-options': [],
    'terms': [],
    'shipping-methods': [],
    'shipping-carriers': [],
    'types': [],
    'directions': [],
    'line-of-businesses': [],
    'registration-types': [],
    'file-types': []
  };
  stateOptions: any[] = [];
  calculatedTaxType: string | null = null;
  currentClientInfo: any = null;
  currentSubsidiaryInfo: any = null;
  private readonly taxOverrideDependentFields: string[] = [
    'registration_type',
    'subsidiary_gst_number',
    'customer_gst_number',
    'registration_no',
    'place_of_supply_id',
    "place_of_supply"
  ];
  private readonly nonEditableFields: string[] = ['order_date', 'client_id', 'form_template_id', 'subsidiary_id', 'location', 'posting_period'];
  selectedShippingAddressId: string | null = null;
  selectedSubsidiaryId: string | null = null;
  selectedBillingAddressId: string | null = null;
  selectedLocationId: string | null = null;
  selectedPlaceOfSupplyId: string | null = null;
  subsidiariesData: any[] = [];
  locationOptions: any[] = [];
  salesTypeOptions: any[] = [];
  lineOfBusinessMap: Record<string, string> = {}; // helper lob map<id, name>

  // Audit logs support (view mode only)
  auditLogs: any[] = [];
  auditLogPage: number = 1;
  auditLogTotalPages: number = 1;
  auditLogTotal: number = 0;
  auditLogLoading: boolean = false;

  // File names for form field files (key: fieldName, value: file name)
  formFieldFileNamesMap: Map<string, string> = new Map();

  readonly SUB_TAB_KEYS = {
    ITEMS: 'items',
    EXPENSES: 'expenses',
    FILES: 'files',
    USER_NOTES: 'user_notes',
    AUDIT_FILTERS: 'audit_filters',
    AUDIT_LOGS: 'audit_logs',
  };

  tabMasterData: any[] = [];
  TAB_ORDER: Array<{ id: number; name: string }> = [];
  TAB_MASTER_DATA: Array<{
    id: number;
    name: string;
    is_active: boolean;
    is_deleted: boolean;
    sub_tabs: Array<{
      id: number;
      tab_id: number;
      name: string;
      show_heading: boolean;
      type: 'FIELD' | 'TABLE';
      is_active: boolean;
      is_deleted: boolean;
      key: string;
    }>;
  }> = [];

  tableData: any =
    [
      { name: "Subtotal", amount: "0" },
      { name: "Tax Total", amount: "0" },
    ]

  constructor(
    private fb: FormBuilder,
    private templateService: TemplateService,
    private itemService: ItemService,
    private activatedRoute: ActivatedRoute,
    private clientOrderService: ClientOrderService,
    private toasterService: ToasterService,
    private router: Router,
    private clientService: ClientOnboardingService,
    private clientListService: ClientListService,
    private mastersService: MastersService,
    private cdr: ChangeDetectorRef,
    private payloadGeneratorService: PayloadGeneratorService,
    private s3FileUploadService: S3FileUploadService,
    private pageActionService: PageActionService,
    private pdfApiService: PdfApiService
  ) { }

  // CLIENT TYPEAHEAD METHODS (similar to vendor-payment pattern)

  /**
   * Ensure client field is configured as autocomplete
   */
  private ensureClientAutocompleteInitialized(): void {
    this.applyToClientFields(field => {
      field.field_type = 'autocomplete';
      if (!field.options) {
        field.options = [];
      }
    });
  }

  /**
   * Update client autocomplete options
   */
  private updateClientAutocompleteOptions(options: any[]): void {
    this.applyToClientFields(field => {
      field.options = options;
    });
    this.cdr.markForCheck();
  }

  /**
   * Apply callback to all client_id fields
   */
  private applyToClientFields(callback: (field: FieldConfig) => void): void {
    const processed = new Set<FieldConfig>();
    const register = (field?: FieldConfig) => {
      if (field && !processed.has(field)) {
        processed.add(field);
        callback(field);
      }
    };
    register(this.primaryFields?.find(f => f.field_name === 'client_id'));
    this.data
      .filter(field => field.field_name === 'client_id')
      .forEach(register);
  }

  /**
   * Handle client search using typeahead utility
   */
  private async handleClientSearch(query: string): Promise<void> {
    const clientKey = this.getFieldKey('client_id');
    const clientControl = this.customerForm?.get(clientKey);
    const currentClientId = clientControl ? this.normalizeClientId(clientControl.value) : null;

    // Use typeahead utility to search clients
    const result = await ClientTypeaheadUtils.searchClients(
      query,
      this.clientListService,
      currentClientId,
      this.clientOptions
    );

    // Update clientOptions array with search results
    this.clientOptions = result.options;

    // Update autocomplete options (show search results)
    const searchOptions = result.options.filter(opt =>
      !currentClientId || String(opt.value) !== String(currentClientId)
    );
    this.updateClientAutocompleteOptions(searchOptions);

    // Ensure control value is a client object, not just an ID
    if (clientControl && result.selectedClient) {
      clientControl.setValue(result.selectedClient, { emitEvent: false });
    }
  }

  /**
   * Normalize client ID from various formats (object, string, number)
   */
  private normalizeClientId(value: any): string | number | null {
    return ClientTypeaheadUtils.normalizeClientId(value);
  }


  async ngOnInit() {
    this.isLoading = true;
    const params = this.activatedRoute.snapshot.queryParams;
    const soId = this.activatedRoute.snapshot.queryParamMap.get('soId');
    if (soId) {
      this.clientSOId = soId;
    }
    if (params['mode']) {
      switch (params['mode']) {
        case 'view':
          this.mode = 'view';
          this.viewMode = true;
          break;
        case 'edit':
          this.mode = 'edit';
          this.viewMode = false;
          break;
        case 'duplicate':
          this.mode = 'duplicate';
          this.viewMode = false;
          break;
        default:
          this.mode = 'create';
          this.viewMode = false;
      }
    } else {
      this.mode = this.clientSOId ? (this.mode == 'view' ? 'view' : 'edit') : 'create';
      this.viewMode = false;
    }
    
    // Register page action handlers
    this.pageActionService.handleActionsUntil(this.destroy$, {
      edit: () => this.onEditSO(),
      duplicate: () => this.onDuplicateSO(),
      'create-si': () => this.onCreateSI(),
      delete: () => this.onDeleteSO(),
      print: () => this.onPrintSO(),
    });
    
    await this.loadTemplateAndData();

    // After initial data load, fetch audit logs in view mode only
    if (this.mode === 'view' && this.clientSOId) {
      await this.loadAuditLogs();
    }
    
    this.setupPageActions();
    this.isLoading = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.pageActionService.setActions([]);
  }

  /**
   * Loads the form template and Client data (if editing), then initializes the form with correct values.
   */
  async loadTemplateAndData() {
    try {
      await this.loadClients();
      await this.loadFormTemplates();
      await this.loadMasters();
      await this.loadPackagingMasters();

      if (this.clientSOId) {
        const soResponse = await this.getClientOrderById(this.clientSOId);
        const orderData = soResponse?.data ?? soResponse ?? null;
        if (orderData) {
          // Store order data for close operation
          this.soData = orderData;

          // Store approval_status from API response
          this.approvalStatus = orderData?.approval_status || 'not_required';

          // Store SO status from API response
          this.soStatus = orderData?.status || 'open';

          // Track cancelled status - check multiple possible locations in response
          const isCancelledValue = orderData?.is_cancelled ?? orderData?.client_order?.is_cancelled;
          this.isCancelled = isCancelledValue === true || isCancelledValue === 1;

          // Disable editing if order is approved, closed, billed, or cancelled (but not in duplicate mode)
          if (this.mode !== 'duplicate' && (this.approvalStatus === 'approved' || this.soStatus === 'closed' || this.soStatus === 'billed' || this.isCancelled)) {
            this.viewMode = true;
            this.mode = 'view';
          }

          // Refresh page actions after setting isCancelled
          this.setupPageActions();

          const formTemplateId = this.normalizeIdValue(orderData?.form_template_id);
          if (formTemplateId) {
            this.currentTemplateId = formTemplateId;
          }

          const templateResponse = await this.fetchTemplateForCurrentSelection();
          if (templateResponse) {
            this.processTemplateResponse(templateResponse);
            await this.populateFormWithOrderData(orderData);
          }
        }
      } else {
        const templateResponse = await this.fetchTemplateForCurrentSelection();
        if (templateResponse) {
          this.processTemplateResponse(templateResponse);
        }
      }
    } catch (err: any) {
      console.error('Error loading template and data:', err);
      this.data = [];
      this.primaryFields = [];
      this.dynamicFormTabs = [];
    }
  }

  private processTemplateResponse(templateResponse: any): void {
    this.customerForm = this.fb.group({})
    this.templates = templateResponse;

    // Build TAB_ORDER and TAB_MASTER_DATA from backend tabs at the start
    const backendTabs = templateResponse?.tabs || templateResponse?.data?.tabs;
    if (backendTabs) {
      this.buildTabMasterDataFromBackend(backendTabs);
    }

    const templateIdFromResponse = this.normalizeIdValue(
      templateResponse?.data?.template?.id ??
      templateResponse?.template?.id ??
      templateResponse?.data?.id
    );
    if (templateIdFromResponse) {
      this.currentTemplateId = templateIdFromResponse;
    }

    const templateData = templateResponse?.data ?? templateResponse ?? null;
    const rawFields = Array.isArray(templateData?.fields) ? templateData.fields : [];
    this.data = rawFields.filter(
      (field: FieldConfig) => !field.is_deleted && field.is_active && field.is_visible
    );

    // Normalize options for ALL fields (regular and custom) that have options
    this.data = this.data.map((f: any) => ({
      ...f,
      options: this.normalizeOptions(f.options)
    }));

    this.data.forEach(field => {
      if (field.field_name === 'subsidiary_gst_number' || field.field_name === 'customer_gst_number') {
        field.field_type = 'select';
        if (!field.options) {
          field.options = [];
        }
      }
    });

    this.primaryFields = this.data.filter((field: FieldConfig) =>
      this.idsMatch(field.tab_id, 1)
    );
    this.customFields = this.data.filter((field: FieldConfig) => field.is_custom_field && !field.is_deleted && field.is_active && field.tab_id != 3);
    const formTemplateField = [...this.data].find(
      (field: FieldConfig) => field.field_name === 'form_template_id'
    );
    if (formTemplateField) {
      formTemplateField.options = this.getTemplateOptionEntries();
      if (this.currentTemplateId) {
        formTemplateField.default_value = this.currentTemplateId;
      }
    }

    this.initializeForm();
    this.buildDynamicFormTabs();
    this.applyTemplateOptionsToField();
    this.applyClientOptionsToField();
    this.setFormTemplateControlValue(this.currentTemplateId);
    this.bindMastersToFields();
    this.syncTaxOverrideDependentFieldsFromForm();
  }


  /**
   * Initializes the form. If customerData is provided, uses it to set initial values. Otherwise uses field defaults.
   */
  initializeForm(customerData?: any) {
    const formControls: Record<string, any> = {};

    // Always add 'id' field for patching and submission
    formControls['id'] = [customerData?.id ?? ''];

    const addFieldControl = (field: FieldConfig) => {
      if (!field || typeof field.field_name !== 'string') return;
      if (String(field.field_type || '').toLowerCase() === 'table') return;

      const key = this.getFieldKey(field.field_name);
      if (formControls[key]) return;

      const validators = field.is_mandatory ? [Validators.required] : [];
      let value =
        customerData && customerData[key] !== undefined && customerData[key] !== null
          ? customerData[key]
          : this.getFieldDefaultValue(field);

      if ((!this.clientSOId || this.mode === 'duplicate') && field.field_name === 'order_date' && field.field_type === 'date') {
        value = new Date();
      }

      if (!this.clientSOId && field.field_name === 'order_no' && (!customerData || !customerData[key])) {
        value = 'System Generated';
      }

      if (field.field_name === 'form_template_id') {
        const normalizedValue = this.normalizeIdValue(
          customerData ? customerData[key] : value ?? this.currentTemplateId
        );
        if (!normalizedValue && this.currentTemplateId) {
          value = this.currentTemplateId;
        } else if (normalizedValue) {
          value = normalizedValue;
          this.currentTemplateId = normalizedValue;
        } else {
          const options = this.getTemplateOptionEntries();
          if (options.length > 0) {
            value = options[0].value;
            this.currentTemplateId = options[0].value;
          }
        }
      }

      formControls[key] = [value, validators];
    };

    (this.primaryFields ?? []).forEach(addFieldControl);

    const secondaryFields = (this.data ?? []).filter(
      (field) => !this.idsMatch(field.tab_id, 1)
    );
    secondaryFields.forEach(addFieldControl);

    this.customerForm = this.fb.group(formControls);
    this.initializeTableFormArrays();
    this.disableNonEditableFields();
  }

  private getFieldDefaultValue(field: FieldConfig): any {
    if (field?.field_name === 'tax_type' && this.calculatedTaxType) {
      return this.calculatedTaxType;
    }
    // Add custom logic for specific field types
    switch (field.field_type) {
      case 'checkbox':
        return false;
      case 'number':
        return 0;
      case 'date':
      case 'datetime':
      case 'time':
        return null;
      case 'select':
        return null;
      case 'radio':
        return null;
      default:
        return field.default_value || '';
    }
  }

  private normalizeOptions(options: any): any[] {
    if (!options) return [];
    if (Array.isArray(options)) {
      return options.map((opt: any) => {
        if (opt && typeof opt === 'object') {
          const label = opt.label ?? opt.name ?? opt.display_name ?? String(opt.id ?? opt.value ?? '');
          let value = opt.value ?? opt.id ?? opt.code ?? label;
          // Preserve large IDs by keeping as string to avoid JS precision loss
          if (typeof value === 'number') value = String(value);
          return { ...opt, label, value, display_name: opt.display_name };
        }
        return { label: String(opt), value: opt };
      });
    }
    if (typeof options === 'object') {
      return Object.keys(options).map(k => ({ label: String((options as any)[k]), value: k }));
    }
    return [];
  }

  private slugify(text: string): string {
    if (!text) {
      return '';
    }
    return text
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/(^_|_$)/g, '');
  }

  private templateTabHasKey(tab: any, keys: string | string[]): boolean {
    if (!tab) {
      return false;
    }

    const keyList = Array.isArray(keys) ? keys : [keys];

    // Check sub_tabs (from TAB_MASTER_DATA/backend)
    if (Array.isArray(tab.sub_tabs)) {
      return tab.sub_tabs.some((sub: any) => {
        const subKey = sub.key || this.slugify(sub.name || '');
        return keyList.includes(subKey);
      });
    }

    return false;
  }

  private isAuditTab(tab: any): boolean {
    return this.templateTabHasKey(tab, [this.SUB_TAB_KEYS.AUDIT_FILTERS, this.SUB_TAB_KEYS.AUDIT_LOGS]);
  }

  /**
   * Build TAB_ORDER and TAB_MASTER_DATA from backend tabs data
   * This should be called at the start of processTemplateResponse
   */
  private buildTabMasterDataFromBackend(backendTabs: any[]): void {
    if (!backendTabs || !Array.isArray(backendTabs)) {
      return;
    }

    // Filter: active and not deleted, then sort by display_order
    const filteredTabs = backendTabs
      .filter((tab: any) => tab.is_active && !tab.is_deleted && tab.system_name !== 'primary_information')
      .sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0));

    // Build TAB_ORDER
    this.TAB_ORDER = filteredTabs.map((tab: any) => ({
      id: Number(tab.tab_id),
      name: tab.display_name || tab.system_name || '',
    }));

    // Build TAB_MASTER_DATA
    this.TAB_MASTER_DATA = filteredTabs.map((tab: any) => {
      // Filter and sort sub_tabs
      const filteredSubTabs = (tab.sub_tabs || [])
        .filter((subTab: any) => subTab.is_active && !subTab.is_deleted)
        .sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0));

      return {
        id: Number(tab.tab_id),
        name: tab.display_name || tab.system_name || '',
        is_active: tab.is_active,
        is_deleted: tab.is_deleted,
        sub_tabs: filteredSubTabs.map((subTab: any) => {
          // Normalize backend keys to match frontend constants
          let normalizedKey = subTab.key || subTab.system_name || '';
          if (normalizedKey === 'audit_logs_filters') {
            normalizedKey = 'audit_filters';
          }

          return {
            id: Number(subTab.sub_tab_id),
            tab_id: Number(tab.tab_id),
            name: subTab.display_name || subTab.system_name || subTab.key || '',
            title: subTab.display_name || subTab.system_name || subTab.key || '',
            show_heading: Boolean(subTab.show_heading),
            type: (subTab.type === 'TABLE' || subTab.type === 'table') ? 'TABLE' : 'FIELD' as 'FIELD' | 'TABLE',
            is_active: subTab.is_active,
            is_deleted: subTab.is_deleted,
            key: normalizedKey,
          };
        }),
      };
    });

    // Update tabMasterData property (used internally)
    this.tabMasterData = this.TAB_MASTER_DATA;
  }

  private buildDynamicFormTabs(): void {
    // Use TAB_MASTER_DATA if available (from backend), otherwise fallback to tabMasterData
    const tabsSource = this.TAB_MASTER_DATA.length > 0 ? this.TAB_MASTER_DATA : this.tabMasterData;

    if (!Array.isArray(tabsSource) || tabsSource.length === 0) {
      this.dynamicFormTabs = [];
      return;
    }

    // Exclude primary information tab (id 1)
    let filteredTabs = tabsSource.filter((tab: any) => !this.idsMatch(tab?.id, 1));

    // Filter audit tab based on mode (only show in view mode)
    if (this.mode !== 'view') {
      filteredTabs = filteredTabs.filter((tab: any) => !this.isAuditTab(tab));
    }

    const tabs = filteredTabs.map((tab: any, tabIndex: number) => {
      const subTabs = (tab.sub_tabs || []).map((subTab: any, subIndex: number) => {
        const fieldsForSubTab = this.data.filter((field: FieldConfig) => {
          const sameTab = this.idsMatch(field.tab_id, tab.id);
          const sameSubTab = this.idsMatch(field.sub_tab_id, subTab.id);
          const isDefaultSubTab =
            (field.sub_tab_id === null ||
              field.sub_tab_id === undefined ||
              Number(field.sub_tab_id) === 0) &&
            subIndex === 0;
          return sameTab && (sameSubTab || isDefaultSubTab);
        });

        const baseKey = subTab.key || this.getFieldKey(subTab.key);
        const semanticKey = (subTab.key || this.getFieldKey(subTab.key || '')).toLowerCase();
        const subTabPayload = {
          id: subTab.id,
          tab_id: subTab.tab_id,
          title: this.capitalize(subTab.name || subTab.title),
          // key is the table control key used by the form array
          key: baseKey,
          is_active: subTab.is_active,
          // sub_tab_key is the semantic key used for identification (items, expenses, audit, etc.)
          sub_tab_key: semanticKey,
          show_heading: subTab.show_heading,
          type: (subTab.type || subTab.sub_type || '').toString().toLowerCase(),
          content: fieldsForSubTab,
          // For audit logs table, attach auditData so dynamic-form knows to render it
          auditData:
            semanticKey === this.SUB_TAB_KEYS.AUDIT_LOGS
              ? this.auditLogs
              : null,
          // Allow filters sub-tab (id 13) to be editable in view mode
          editableInViewMode:
            semanticKey === this.SUB_TAB_KEYS.AUDIT_FILTERS
        };

        if (subTabPayload.type === 'table') {
          const tableKey = subTabPayload.key;
          const formArray = this.getTableFormArray(tableKey);
          if (formArray.length === 0) {
            const newRow = this.createEmptyTableRow(fieldsForSubTab);
            formArray.push(newRow);
          }
        }

        return subTabPayload;
      });
      return {
        title: tab.name,
        id: tab.id,
        is_active: tab.is_active,
        sub_tabs: subTabs
      };
    });
    this.dynamicFormTabs = tabs;
  }

  /**
   * Load audit logs for the client order (view mode only)
   */
  private async loadAuditLogs(): Promise<void> {
    if (!this.clientSOId || this.mode !== 'view') {
      return;
    }

    try {
      this.auditLogPage = 1;
      const auditResponse = await lastValueFrom(
        this.clientOrderService.getAuditLogs(this.clientSOId, { limit: 20, offset: 0 })
      );
      if (auditResponse?.data?.audit_logs) {
        this.auditLogs = auditResponse.data.audit_logs;
        this.auditLogTotal = auditResponse.data.total || 0;
        this.auditLogTotalPages = Math.ceil(this.auditLogTotal / 20) || 1;
        // Rebuild tabs so that audit tab gets auditData bound
        this.buildDynamicFormTabs();
        this.updateAuditLogsFormArray();
      } else if (auditResponse?.audit_logs) {
        this.auditLogs = auditResponse.audit_logs;
        this.auditLogTotal = auditResponse.data?.total || 0;
        this.auditLogTotalPages = Math.ceil(this.auditLogTotal / 20) || 1;
        this.buildDynamicFormTabs();
        this.updateAuditLogsFormArray();
      } else {
        this.auditLogs = [];
        this.auditLogTotal = 0;
        this.auditLogTotalPages = 1;
        this.updateAuditLogsFormArray();
      }
    } catch (error) {
      console.error('Error loading audit logs:', error);
      this.auditLogs = [];
      this.auditLogTotal = 0;
      this.auditLogTotalPages = 1;
    }
  }

  /**
   * Handle audit log filtering from filter fields in Audit tab
   */
  private async handleAuditLogFiltering(key: string, value: any, event: any): Promise<void> {
    try {
      if (!this.clientSOId) {
        console.warn('No client SO ID available for filtering');
        return;
      }

      this.auditLogPage = 1;
      const filterParams: any = { limit: 20, offset: 0 };

      // Normalize key to handle different naming styles
      const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '_');

      if ((normalizedKey === 'audit_date' || normalizedKey.includes('date')) && value) {
        const dateValue =
          value instanceof Date ? moment(value).format('YYYY-MM-DD') : value;
        filterParams.date = dateValue;
      }

      const response = await lastValueFrom(
        this.clientOrderService.getAuditLogs(this.clientSOId, filterParams)
      );

      if (response?.data?.audit_logs) {
        this.auditLogs = response.data.audit_logs;
        this.auditLogTotal = response.data.total || 0;
        this.auditLogTotalPages = Math.ceil(this.auditLogTotal / 20) || 1;
        this.updateAuditLogsFormArray();
      } else if (response?.audit_logs) {
        this.auditLogs = response.audit_logs;
        this.auditLogTotal = 0;
        this.auditLogTotalPages = 1;
        this.updateAuditLogsFormArray();
      } else {
        this.auditLogs = [];
        this.auditLogTotal = 0;
        this.auditLogTotalPages = 1;
        this.updateAuditLogsFormArray();
      }
    } catch (error) {
      console.error('Error filtering audit logs:', error);
    }
  }

  /**
   * Page-based audit log navigation
   */
  async onAuditPageChange(page: number): Promise<void> {
    if (!this.clientSOId || this.auditLogLoading || page < 1 || page > this.auditLogTotalPages) return;

    this.auditLogLoading = true;
    try {
      const offset = (page - 1) * 20;
      const filterParams: any = { limit: 20, offset };

      const auditDateKey = this.getFieldKey('audit_date');
      const auditDateValue = this.customerForm?.get(auditDateKey)?.value;
      if (auditDateValue) {
        filterParams.date = auditDateValue instanceof Date
          ? moment(auditDateValue).format('YYYY-MM-DD')
          : auditDateValue;
      }

      const response = await lastValueFrom(
        this.clientOrderService.getAuditLogs(this.clientSOId, filterParams)
      );

      if (response?.data?.audit_logs) {
        this.auditLogs = response.data.audit_logs;
        this.auditLogTotal = response.data.total || this.auditLogTotal;
        this.auditLogTotalPages = Math.ceil(this.auditLogTotal / 20) || 1;
        this.auditLogPage = page;
        this.updateAuditLogsFormArray();
      }
    } catch (error) {
      console.error('Error loading audit logs page:', error);
    } finally {
      this.auditLogLoading = false;
    }
  }

  /**
   * Update audit logs FormArray when audit data changes
   */
  private updateAuditLogsFormArray(): void {
    if (!this.customerForm) {
      return;
    }

    for (const tab of this.dynamicFormTabs) {
      for (const subTab of tab.sub_tabs) {
        if (subTab.auditData) {
          const tableKey = subTab.key;
          let existingArray = this.customerForm.get(tableKey) as FormArray;

          // Create FormArray if it does not exist yet
          if (!existingArray) {
            existingArray = this.fb.array([]);
            this.customerForm.addControl(tableKey, existingArray);
          }

          // Clear existing rows
          while (existingArray.length > 0) {
            existingArray.removeAt(0);
          }

          if (this.auditLogs && this.auditLogs.length > 0) {
            this.auditLogs.forEach((auditLog: any) => {
              const rowGroup = this.fb.group({});

              Object.keys(auditLog).forEach(key => {
                rowGroup.addControl(this.getFieldKey(key), this.fb.control(auditLog[key]));
              });

              existingArray.push(rowGroup);
            });
          }

          this.cdr.detectChanges();
          break;
        }
      }
    }
  }



  // getFormTemplate and old initializeForm are now handled by loadTemplateAndData
  getFieldKey(fieldName: string): string {
    return fieldName.toLowerCase().replace(/[^a-z0-9]/g, '_');
  }

  getSuggestionsMapForTable(tableKey: string): Map<string, any[]> {
    const filtered = new Map<string, any[]>();
    if (!tableKey) {
      return filtered;
    }

    const prefix = `${tableKey}_`;
    this.searchSuggestionsMap.forEach((value, key) => {
      if (key.startsWith(prefix)) {
        filtered.set(key.substring(prefix.length), value);
      }
    });
    return filtered;
  }

  async onSearchQuery(
    event: { fieldName: string; query: string; rowIndex: number; rowId?: any },
    subTab: any
  ) {
    if (!event || !event.fieldName) {
      return;
    }
    const { fieldName, query, rowIndex } = event;
    const tableKey = subTab?.key;
    if (!query || !tableKey) {
      const key = `${tableKey}_${rowIndex}_${fieldName}`;
      this.searchSuggestionsMap.set(key, []);
      this.searchSuggestionsMap = new Map(this.searchSuggestionsMap);
      return;
    }

    try {
      const response = await lastValueFrom(
        this.itemService.searchItems({
          name: query.trim(),
          limit: 20,
          is_deleted: false,
          get_stock_details: true
        })
      );

      const items = response?.data?.items || response?.data?.item_masters || [];
      const options = items.map((item: any) => ({
        label: item.item_master_name || item.name || item.item_name || item.product_code || String(item.id || ''),
        value: String(item.id || item.item_id || ''),
        id: item.id,
        name: item.item_master_name || item.name || item.item_name,
        description: item.item_master_description || item.description,
        item_master_name: item.item_master_name,
        item_master_description: item.item_master_description,
        tax_rate: item?.gst_percentage,
        item_master_id: item.item_master_id,
        total_current_quantity: item?.total_current_quantity,
        recent_rate: item?.recent_rate,
        ...item
      }));

      const key = `${tableKey}_${rowIndex}_${fieldName}`;
      this.searchSuggestionsMap.set(key, options);
      this.searchSuggestionsMap = new Map(this.searchSuggestionsMap);
    } catch (error) {
      const key = `${tableKey}_${rowIndex}_${fieldName}`;
      this.searchSuggestionsMap.set(key, []);
      this.searchSuggestionsMap = new Map(this.searchSuggestionsMap);
      console.error('Error fetching item suggestions:', error);
    }
  }

  getTableFormArray(key: string): FormArray {
    if (!key) return this.fb.array([]);

    let existing = this.customerForm?.get(key);
    if (existing && existing instanceof FormArray) {
      if (!this.tableFormArrays.has(key)) {
        this.tableFormArrays.set(key, existing);
      }
      return existing;
    }

    if (this.tableFormArrays.has(key)) {
      const memoized = this.tableFormArrays.get(key)!;
      if (this.customerForm && !this.customerForm.get(key)) {
        this.customerForm.addControl(key, memoized);
      }
      return memoized;
    }

    const formArray = this.fb.array([]);
    this.tableFormArrays.set(key, formArray);
    if (this.customerForm) {
      this.customerForm.addControl(key, formArray);
    }
    return formArray;
  }

  private async populateFormWithOrderData(orderData: any): Promise<void> {
    if (!orderData || !this.customerForm) {
      return;
    }

    const flattenedOrder = this.flattenOrderData(orderData);
    if (flattenedOrder) {
      const templateKey = this.getFieldKey('form_template_id');
      const flattenedTemplateId = flattenedOrder[templateKey];
      if (flattenedTemplateId) {
        this.currentTemplateId = this.normalizeIdValue(flattenedTemplateId);
      }
    }
    if (Object.keys(flattenedOrder).length > 0) {
      // In duplicate mode, always use today's date
      if (this.mode === 'duplicate') {
        flattenedOrder[this.getFieldKey('posting_date')] = new Date();
      }
      this.customerForm.patchValue(flattenedOrder, { emitEvent: false });
    }

    this.populateOrderItems(orderData?.order_items || []);
    this.populateUserNotes(orderData?.order_notes || []);
    this.populateOrderFiles(orderData?.order_files || []);

    this.customerForm.updateValueAndValidity({ emitEvent: false });
    this.syncTaxOverrideDependentFieldsFromForm();
    
    // Clear order_no in duplicate mode (will be system generated)
    if (this.mode === 'duplicate') {
      const orderNoKey = this.getFieldKey('order_no');
      const orderNoControl = this.customerForm.get(orderNoKey);
      if (orderNoControl) {
        orderNoControl.setValue('System Generated', { emitEvent: false });
      }
    }
    
    this.disableNonEditableFields();

    const clientFieldKey = this.getFieldKey('client_id');
    const clientIdFromForm =
      this.customerForm.get(clientFieldKey)?.value ?? orderData?.client_id;
    if (clientIdFromForm) {
      await this.handleClientChange(clientIdFromForm, true);
    }

    if (this.selectedPlaceOfSupplyId) {
      const placeOfSupplyKey = this.getFieldKey('place_of_supply_id');
      const placeOfSupplyControl = this.customerForm.get(placeOfSupplyKey);
      if (placeOfSupplyControl) {
        // Find matching option for proper display
        const placeOfSupplyField = this.primaryFields?.find((f: any) => f.field_name === 'place_of_supply_id') ||
                                   this.data?.find((f: any) => f.field_name === 'place_of_supply_id');
        let valueToSet = String(this.selectedPlaceOfSupplyId);
        
        if (placeOfSupplyField?.options && Array.isArray(placeOfSupplyField.options)) {
          const matchingOption = placeOfSupplyField.options.find((opt: any) =>
            String(opt.id) === String(this.selectedPlaceOfSupplyId) ||
            String(opt.value) === String(this.selectedPlaceOfSupplyId) ||
            Number(opt.id) === Number(this.selectedPlaceOfSupplyId) ||
            Number(opt.value) === Number(this.selectedPlaceOfSupplyId)
          );
          if (matchingOption) {
            valueToSet = matchingOption.value || matchingOption.id;
          }
        }
        
        placeOfSupplyControl.setValue(valueToSet, { emitEvent: false });
      }
    }
  }

  private disableNonEditableFields(): void {
    if (!this.customerForm || this.mode !== 'edit') {
      return;
    }

    this.nonEditableFields.forEach(fieldName => {
      const control = this.customerForm.get(this.getFieldKey(fieldName));
      if (control) {
        control.disable({ emitEvent: false });
      }
    });
  }

  private flattenOrderData(orderData: any): Record<string, any> {
    const flattened: Record<string, any> = {};
    const assign = (fieldName: string, value: any, transform?: (val: any) => any) => {
      const key = this.getFieldKey(fieldName);
      if (!this.customerForm?.get(key)) {
        return;
      }
      const transformedValue = transform ? transform(value) : value;
      if (transformedValue === undefined) {
        return;
      }
      flattened[key] = transformedValue;
    };

    assign('id', orderData?.id ?? '');
    assign('client_id', orderData?.client_id, (val) =>
      val !== undefined && val !== null ? String(val) : null
    );
    assign('order_no', orderData?.order_no ?? '');
    assign('order_date', this.mode === 'duplicate' ? new Date() : orderData?.order_date, (val) => this.mode === 'duplicate' ? val : this.parseDate(val));
    assign('memo', orderData?.memo ?? '');
    assign('form_template_id', orderData?.form_template_id ?? null);
  
    // Handle place_of_supply - enforce clean string ID assignment
    const placeOfSupplyId = orderData?.place_of_supply_id ?? orderData?.place_of_supply;
    if (placeOfSupplyId) {
      assign('place_of_supply_id', String(placeOfSupplyId));
    } else {
      assign('place_of_supply_id', null);
    }

    const orderDetails = orderData?.order_details ?? {};
    assign('subsidiary_gst_number', orderDetails?.subsidiary_gst_number ?? '');
    assign('customer_gst_number', orderDetails?.customer_gst_number ?? '');
    assign('registration_type', orderDetails?.registration_type ?? null);
    assign('registration_no', orderDetails?.registration_no ?? '');
    assign('tax_details_override', orderDetails?.tax_details_override ?? false, (val) =>
      Boolean(val)
    );
    assign('terms', orderDetails?.terms ?? '');
    assign('courier_name', orderDetails?.courier_name ?? '');
    assign('shipping_date', orderDetails?.shipping_date, (val) => this.parseDate(val));
    assign('shipping_carrier', orderDetails?.shipping_carrier ?? '');
    assign('shipping_method', orderDetails?.shipping_method ?? '');
    assign('delivery_date', orderDetails?.delivery_date, (val) => this.parseDate(val));
    assign('ship_to', orderDetails?.ship_to_id ?? null);
    assign('bill_to', orderDetails?.billing_address ?? null);
    // Handle project and product fields - convert to string for dropdown matching
    assign('project', orderData?.project, (val) =>
      val !== undefined && val !== null && val !== 0 ? String(val) : null
    );
    assign('product', orderData?.product, (val) =>
      val !== undefined && val !== null && val !== 0 ? String(val) : null
    );
    // Handle sales_type - convert numeric ID to option value for dropdown matching
    const salesTypeValue = orderData?.sales_type;
    if (salesTypeValue !== undefined && salesTypeValue !== null && salesTypeValue !== 0) {
      const matchingSalesType = this.salesTypeOptions.find((opt: any) =>
        String(opt.id) === String(salesTypeValue) ||
        String(opt.value) === String(salesTypeValue)
      );
      if (matchingSalesType) {
        assign('sales_type', matchingSalesType.id || matchingSalesType.value);
      } else {
        assign('sales_type', salesTypeValue);
      }
    }
    this.selectedSubsidiaryId = orderData?.subsidiary_id ?? null;
    this.selectedLocationId = orderData?.location ?? null;
    this.selectedPlaceOfSupplyId = orderData?.place_of_supply_id ?? null;
    this.selectedShippingAddressId = orderData?.ship_to_id ?? null;
    this.selectedBillingAddressId = orderDetails?.billing_address ?? null;

    const customFieldValues = orderData?.custom_fields ?? {};
    if (customFieldValues && typeof customFieldValues === 'object' && Array.isArray(this.customFields)) {
      this.customFields.forEach((field: FieldConfig) => {
        const fieldName = field?.field_name;
        if (!fieldName) {
          return;
        }
        assign(fieldName, customFieldValues[fieldName]);
      });
    }
    return flattened;
  }

  private parseDate(value: any): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private populateOrderItems(orderItems: any[]): void {
    const itemsConfig = this.getItemsTableConfig();
    if (!itemsConfig) {
      return;
    }

    const itemsArray = this.getTableFormArray(itemsConfig.key);
    this.clearFormArray(itemsArray);

    if (!Array.isArray(orderItems) || orderItems.length === 0) {
      if (itemsArray.length === 0) {
        const emptyRow = this.createEmptyTableRow(itemsConfig.fields);
        itemsArray.push(emptyRow);
      }
      itemsArray.updateValueAndValidity({ emitEvent: false });
      return;
    }

    orderItems.forEach((item: any) => {
      const initialData = this.buildItemRowInitialData(item);
      const rowGroup = this.createTableRowFromFields(itemsConfig.fields, initialData);
      if (rowGroup.get('id')) {
        rowGroup.patchValue({ id: item?.id ? String(item.id) : '' }, { emitEvent: false });
      }
      if (rowGroup.get('item_id')) {
        rowGroup.patchValue({ item_id: item?.item_id ? String(item.item_id) : '' }, { emitEvent: false });
      }
      if (rowGroup.get('item_category_id')) {
        rowGroup.patchValue({ item_category_id: item?.item_category_id ?? null }, { emitEvent: false });
      }
      if (rowGroup.get('is_deleted')) {
        rowGroup.patchValue({ is_deleted: item?.is_deleted ?? false }, { emitEvent: false });
      }
      itemsArray.push(rowGroup);

      // Apply pack/units/scheme field state based on item_category_id during initial load
      this.applyItemCategoryFieldState(rowGroup, item?.item_category_id);
    });
    itemsArray.updateValueAndValidity({ emitEvent: false });
    this.tableFormArrays.set(itemsConfig.key, itemsArray);
    this.triggerInitialCalculationsForItems(itemsConfig.key, itemsArray);
  }

  private populateUserNotes(userNotes: any[]): void {
    const userNotesConfig = this.getUserNotesTableConfig();
    if (!userNotesConfig) {
      return;
    }

    const notesArray = this.getTableFormArray(userNotesConfig.key);
    this.clearFormArray(notesArray);

    if (!Array.isArray(userNotes) || userNotes.length === 0) {
      if (notesArray.length === 0) {
        const emptyRow = this.createEmptyTableRow(userNotesConfig.fields);
        notesArray.push(emptyRow);
      }
      notesArray.updateValueAndValidity({ emitEvent: false });
      return;
    }
    userNotes.forEach((note: any) => {
      const initialData = this.buildUserNoteRowInitialData(note);
      const rowGroup = this.createTableRowFromFields(userNotesConfig.fields, initialData);
      if (rowGroup.get('id')) {
        rowGroup.patchValue({ id: note?.id ? String(note.id) : '' }, { emitEvent: false });
      }
      if (rowGroup.get('is_deleted')) {
        rowGroup.patchValue({ is_deleted: note?.is_deleted ?? false }, { emitEvent: false });
      }
      notesArray.push(rowGroup);
    });
    notesArray.updateValueAndValidity({ emitEvent: false });
    this.tableFormArrays.set(userNotesConfig.key, notesArray);
  }

  private getItemsTableConfig(): { key: string; fields: FieldConfig[] } | null {
    if (!Array.isArray(this.dynamicFormTabs)) {
      return null;
    }

    for (const tab of this.dynamicFormTabs) {
      if (!Array.isArray(tab?.sub_tabs)) {
        continue;
      }
      for (const subTab of tab.sub_tabs) {
        if ((subTab?.type || '').toString().toLowerCase() !== 'table') {
          continue;
        }
        const fields: FieldConfig[] = subTab?.content || [];

        // Prefer semantic sub_tab_key when available
        const subTabKey = (subTab?.sub_tab_key || '').toString().toLowerCase();
        if (subTabKey === this.SUB_TAB_KEYS.ITEMS) {
          const key = subTab?.key;
          return { key, fields };
        }

        const hasItemField = fields.some(
          (field: FieldConfig) =>
            field?.field_name === 'item_id' ||
            this.getFieldKey(field?.field_name || '') === this.getFieldKey('item_id')
        );
        if (hasItemField || (subTabKey && subTabKey.includes('item'))) {
          const key = subTab?.key;
          return { key, fields };
        }
      }
    }

    return null;
  }

  private getUserNotesTableConfig(): { key: string; fields: FieldConfig[] } | null {
    if (!Array.isArray(this.dynamicFormTabs)) {
      return null;
    }

    for (const tab of this.dynamicFormTabs) {
      if (!Array.isArray(tab?.sub_tabs)) {
        continue;
      }
      for (const subTab of tab.sub_tabs) {
        if ((subTab?.type || '').toString().toLowerCase() !== 'table') {
          continue;
        }
        // Prefer semantic sub_tab_key when available
        const subTabKey = (subTab?.sub_tab_key || '').toString().toLowerCase();
        const isUserNotesByKey = subTabKey === this.SUB_TAB_KEYS.USER_NOTES;

        // Fallback: detect by key if sub_tab_key not present
        const isUserNotesByKeyFallback =
          !isUserNotesByKey &&
          subTab?.key &&
          subTab.key.toLowerCase().includes('user_notes');

        if (isUserNotesByKey || isUserNotesByKeyFallback) {
          const key = subTab?.key;
          const fields: FieldConfig[] = subTab?.content || [];
          return { key, fields };
        }
      }
    }

    return null;
  }

  private clearFormArray(formArray: FormArray | null): void {
    if (!formArray) {
      return;
    }
    while (formArray.length > 0) {
      formArray.removeAt(formArray.length - 1);
    }
  }

  private triggerInitialCalculationsForItems(tableKey: string, tableArray: FormArray): void {
    if (!tableArray || tableArray.length === 0) {
      return;
    }

    tableArray.controls.forEach((control, index) => {
      if (!(control instanceof FormGroup)) {
        return;
      }
      this.calculateItemsTable({
        key: tableKey,
        rowIndex: index,
        fieldName: 'initial_load'
      });
      this.updateItemsSummary();
    });
  }

  private updateItemsSummary() {
    const totals = this.calculateTotals();
    
    // Build summary using baseAmount which correctly handles:
    // - Products (category 1): baseAmount = grossAmount - taxAmount (MRP is inclusive)
    // - Services (category 2): baseAmount = grossAmount (rate is exclusive)
    this.tableData = [
      { name: "Net Amount", amount: (totals.baseAmount + totals.discountAmount).toString() },
      { name: "Discount Amount", amount: totals.discountAmount.toString() },
      { name: "Gross Amount", amount: totals.baseAmount.toString() },
      { name: "Tax Amount", amount: totals.taxAmount.toString() },
      { name: "Total", amount: totals.finalAmount.toString() }
    ];

    // Trigger change detection to update summary display
    this.cdr.detectChanges();
  }

  private buildItemRowInitialData(item: any): Record<string, any> {
    const initialData: Record<string, any> = {};
    if (!item) {
      return initialData;
    }

    const assign = (fieldName: string, value: any, transform?: (val: any) => any) => {
      const key = this.getFieldKey(fieldName);
      const transformedValue = transform ? transform(value) : value;
      if (transformedValue === undefined) {
        return;
      }
      initialData[key] = transformedValue;
    };
    assign('item_id', item?.item_id);
    assign('item_category_id', item?.item_category_id);
    assign('item_name', item?.item_name);
    assign('item_type', item?.item_type);
    assign('quantity', this.toNumberValue(item?.quantity));
    assign('unit_id', item?.unit_id);
    assign('last_purchase_price', this.toNumberValue(item?.last_order_price));
    assign('current_stock', this.toNumberValue(item?.current_stock));
    assign('mrp', this.toNumberValue(item?.mrp));
    assign('rate', this.toNumberValue(item?.rate));
    assign('gross_amount', this.toNumberValue(item?.gross_amount));
    assign('amount', this.toNumberValue(item?.amount));
    assign('tax_rate', this.toNumberValue(item?.tax_rate));
    assign('tax_type', item?.tax_type);
    assign('tax_amount', this.toNumberValue(item?.tax_amount));
    assign('net_amount', this.toNumberValue(item?.net_amount));
    assign('discount_percentage', this.toNumberValue(item?.discount_percentage));
    assign('discount_amount', this.toNumberValue(item?.discount_amount));
    assign('total_amount', this.toNumberValue(item?.total_amount));
    assign('hsn_code', item?.hsn_code);
    assign('uqc', item?.uqc);
    assign('foc', item?.foc);
    assign('pack', item?.pack);
    assign('scheme', item?.scheme);
    assign('description', item?.description);
    assign('line_of_business_id', item?.line_of_business_id);
    assign('project_id', item?.project_id);
    assign('product_id', item?.product_id);
    assign('product_category_id', item?.product_category_id);
    assign('nature_of_good_id', item?.nature_of_good_id);
    assign('location_id', item?.location_id);

    // Handle custom fields from item.custom_fields object
    if (item?.custom_fields && typeof item.custom_fields === 'object') {
      Object.keys(item.custom_fields).forEach(customFieldName => {
        const value = item.custom_fields[customFieldName];
        if (value !== undefined && value !== null) {
          assign(customFieldName, value);
        }
      });
    }

    return initialData;
  }

  private buildUserNoteRowInitialData(note: any): Record<string, any> {
    const initialData: Record<string, any> = {};
    if (!note) {
      return initialData;
    }

    const assign = (fieldName: string, value: any, transform?: (val: any) => any) => {
      const key = this.getFieldKey(fieldName);
      const transformedValue = transform ? transform(value) : value;
      if (transformedValue === undefined) {
        return;
      }
      initialData[key] = transformedValue;
    };

    assign('title', note?.title);
    assign('memo', note?.memo);
    assign('date', note?.date, (val) => this.parseDate(val));
    assign('time', note?.time, (val) => this.parseTimeValue(val));
    assign('type', note?.type);
    assign('direction', note?.direction);

    // Handle custom fields from note.custom_fields object if any
    if (note?.custom_fields && typeof note.custom_fields === 'object') {
      Object.keys(note.custom_fields).forEach(customFieldName => {
        const value = note.custom_fields[customFieldName];
        if (value !== undefined && value !== null) {
          assign(customFieldName, value);
        }
      });
    }

    return initialData;
  }

  private populateOrderFiles(orderFiles: any[]): void {
    const filesConfig = this.getFilesTableConfig();
    if (!filesConfig) {
      return;
    }

    const filesArray = this.getTableFormArray(filesConfig.key);
    this.clearFormArray(filesArray);

    if (!Array.isArray(orderFiles) || orderFiles.length === 0) {
      if (filesArray.length === 0 && filesConfig.fields.length > 0) {
        const emptyRow = this.createEmptyTableRow(filesConfig.fields);
        filesArray.push(emptyRow);
      }
      filesArray.updateValueAndValidity({ emitEvent: false });
      return;
    }

    orderFiles.forEach((file: any) => {
      const initialData = this.buildFileRowInitialData(file);
      const rowGroup = filesConfig.fields.length > 0
        ? this.createTableRowFromFields(filesConfig.fields, initialData)
        : this.createFileTableRow(initialData);
      filesArray.push(rowGroup);
    });
    filesArray.updateValueAndValidity({ emitEvent: false });
    this.tableFormArrays.set(filesConfig.key, filesArray);
    this.cdr.detectChanges();
  }

  /**
   * Create a file table row when field config is empty
   */
  private createFileTableRow(initialData: Record<string, any>): FormGroup {
    const rowGroup = this.fb.group({});

    // Add id and is_deleted controls
    rowGroup.addControl('id', this.fb.control(initialData['id'] || ''));
    rowGroup.addControl('is_deleted', this.fb.control(initialData['is_deleted'] ?? false));

    // Add file-related controls
    const fileKey = this.getFieldKey('file');
    const fileNameKey = this.getFieldKey('file_name');
    const fileTypeKey = this.getFieldKey('file_type');
    const fileSizeKey = this.getFieldKey('size_kb');

    rowGroup.addControl(fileKey, this.fb.control(initialData[fileKey] || ''));
    rowGroup.addControl(fileNameKey, this.fb.control(initialData[fileNameKey] || ''));
    rowGroup.addControl(fileTypeKey, this.fb.control(initialData[fileTypeKey] || ''));
    rowGroup.addControl(fileSizeKey, this.fb.control(initialData[fileSizeKey] || 0));

    return rowGroup;
  }

  private buildFileRowInitialData(file: any): Record<string, any> {
    const initialData: Record<string, any> = {};
    if (!file) {
      return initialData;
    }

    const assign = (fieldName: string, value: any, transform?: (val: any) => any) => {
      const key = this.getFieldKey(fieldName);
      const transformedValue = transform ? transform(value) : value;
      if (transformedValue === undefined) {
        return;
      }
      initialData[key] = transformedValue;
    };

    // Include id and is_deleted in initialData to avoid separate patching
    if (file?.id !== undefined) {
      initialData['id'] = file.id ? String(file.id) : '';
    }
    if (file?.is_deleted !== undefined) {
      initialData['is_deleted'] = file.is_deleted ?? false;
    }

    // Map API response fields to form fields
    assign('file', file?.file);
    assign('file_name', file?.file_name);
    assign('file_type', file?.file_type);
    assign('size_kb', this.toNumberValue(file?.size_kb));

    // Handle custom fields from file.custom_fields object if any
    if (file?.custom_fields && typeof file.custom_fields === 'object') {
      Object.keys(file.custom_fields).forEach(customFieldName => {
        const value = file.custom_fields[customFieldName];
        if (value !== undefined && value !== null) {
          assign(customFieldName, value);
        }
      });
    }

    return initialData;
  }

  private parseTimeValue(value: any): any {
    if (!value) {
      return null;
    }

    // If it's already a Date object, return it as is
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    // If it's a string (ISO datetime from backend), parse it to Date
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }

      // Try to parse as datetime (ISO format from backend)
      const parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime())) {
        return parsed; // Return as Date object for the datetime picker
      }

      return null;
    }

    return null;
  }

  private initializeTableFormArrays(): void {
    if (!this.customerForm) return;

    const tableFields = (this.data ?? []).filter(
      (field) => String(field.field_type || '').toLowerCase() === 'table'
    );

    tableFields.forEach((field) => {
      const key = this.getFieldKey(field.field_name);
      if (!this.customerForm!.get(key)) {
        this.customerForm!.addControl(key, this.fb.array([]));
      }
      this.tableFormArrays.set(key, this.customerForm!.get(key) as FormArray);
    });
  }

  private createEmptyTableRow(fields: FieldConfig[]): FormGroup {
    const row = this.fb.group({});
    fields.forEach((field) => {
      if (String(field.field_type || '').toLowerCase() === 'table') return;
      const key = this.getFieldKey(field.field_name);
      const validators = field.is_mandatory ? [Validators.required] : [];
      row.addControl(key, this.fb.control(this.getFieldDefaultValue(field), validators));
    });
    return row;
  }

  private createTableRowFromFields(fields: FieldConfig[], initialData: Record<string, any>): FormGroup {
    const row = this.createEmptyTableRow(fields);

    if (!row.get('id')) {
      row.addControl('id', this.fb.control(''));
    }
    if (!row.get('is_deleted')) {
      row.addControl('is_deleted', this.fb.control(false));
    }
    if (!row.get('item_id')) {
      row.addControl('item_id', this.fb.control(false));
    }
    if (!row.get('item_category_id')) {
      row.addControl('item_category_id', this.fb.control(false));
    }

    if (initialData && Object.keys(initialData).length > 0) {
      row.patchValue(initialData, { emitEvent: false });
    }

    return row;
  }

  private idsMatch(source: string | number | undefined, target: string | number | undefined): boolean {
    if (source === null || source === undefined || target === null || target === undefined) {
      return false;
    }
    return Number(source) === Number(target);
  }

  private capitalize(value: string | undefined): string {
    if (!value) {
      return '';
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  async onTemplateChange(value: any) {
    if (!value) {
      return;
    }
    try {
      this.isLoading = true;

      // Fetch new template based on selected ID
      const newTemplateResp = await lastValueFrom(
        this.templateService.getSOFormTemplateById(value)
      );

      if (newTemplateResp?.data) {
        // Update the response with new template data
        this.processTemplateResponse(newTemplateResp);
        this.data = newTemplateResp.data.fields.filter(
          (field: FieldConfig) => !field.is_deleted && field.is_active && field.is_visible
        );
      }
      this.updateItemsSummary();
      this.isLoading = false;
    } catch (err) {
      console.error('Error fetching new template:', err);
      this.isLoading = false;
    }
  }

  async getClientOrderById(orderId: string | number) {
    const response = await lastValueFrom(this.clientOrderService.getClientOrderById(orderId));
    // Handle various response structures: response.data.client_order, response.data, or response
    const order = response?.data?.client_order ?? response?.data ?? response;
    return order;
  }

  getApprovalStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      'pending': 'Pending Approval',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'not_required': 'No Approval Required'
    };
    return labels[status] || status;
  }

  getApprovalStatusClass(status: string): string {
    const classes: { [key: string]: string } = {
      'pending': 'warning',
      'approved': 'success',
      'rejected': 'danger',
      'not_required': 'secondary'
    };
    return classes[status] || 'secondary';
  }

  getSOStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      'open': 'Open',
      'partially_complete': 'Partially Complete',
      'partially_billed': 'Partially Billed',
      'billed': 'Billed',
      'closed': 'Closed'
    };
    return labels[status] || 'Open';
  }


  /**
   * Close the Sales Order - marks status as 'closed'
   */
  async closeSO(): Promise<void> {
    if (!this.clientSOId) {
      this.toasterService.warn('Warning', 'No Sales Order to close');
      return;
    }

    if (this.soStatus === 'closed') {
      this.toasterService.warn('Warning', 'Sales Order is already closed');
      return;
    }

    if (!confirm('Are you sure you want to close this Sales Order? This action cannot be undone.')) {
      return;
    }

    try {
      this.isLoading = true;

      // Use stored soData
      const soDataToUpdate = this.soData;
      if (!soDataToUpdate) {
        throw new Error('SO data not available');
      }

      // Send only essential fields required for status update
      // Wrap in client_order object to match backend expected format
      const updatedPayload = {
        client_order: {
          id: soDataToUpdate.id,
          client_id: soDataToUpdate.client_id,
          subsidiary_id: soDataToUpdate.subsidiary_id,
          form_template_id: soDataToUpdate.form_template_id,
          order_date: soDataToUpdate.order_date,
          status: 'closed'
        }
      };

      await lastValueFrom(this.clientOrderService.updateClientOrder(this.clientSOId, updatedPayload));
      this.soStatus = 'closed';
      // Refresh page actions to disable "Create SI" button
      this.setupPageActions();
      this.toasterService.success('Success', 'Sales Order closed successfully');
      // Refresh the page
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { mode: 'view', soId: this.clientSOId },
        queryParamsHandling: 'merge'
      });
    } catch (error: any) {
      console.error('Error closing SO:', error);
      this.toasterService.error('Error', error?.message || 'Failed to close Sales Order');
    } finally {
      this.isLoading = false;
    }
  }

  async onSubmit() {
    if (!this.customerForm) {
      return;
    }

    // Prevent submission if order is approved or closed
    if (this.clientSOId && this.approvalStatus === 'approved' && this.mode=='edit') {
      this.toasterService.warn('Warning', 'Cannot edit sales order with approved status');
      return;
    }
    if (this.clientSOId && (this.soStatus === 'closed' || this.soStatus === 'billed') && this.mode=='edit') {
      this.toasterService.warn('Warning', 'Cannot edit sales order with closed or billed status');
      return;
    }

    // Business validation: there must be at least one item row
    if (!this.hasAtLeastOneItemRow()) {
      this.toasterService.showMessage(
        this.toasterService.MESSAGE_TYPES.ERROR,
        'At least one item is required in the Items table before saving the sales order.'
      );
      return;
    }

    // Business validation: quantity and pack must be greater than 0 in items table
    const invalidQtyAndPackRows = this.getInvalidItemQuantityOrPackRows();
    if (invalidQtyAndPackRows.length > 0) {
      const rowsLabel = invalidQtyAndPackRows.join(', ');
      this.toasterService.showMessage(
        this.toasterService.MESSAGE_TYPES.ERROR,
        `Quantity and pack must be greater than 0 in Items table (row${invalidQtyAndPackRows.length > 1 ? 's' : ''}: ${rowsLabel}). Please enter valid quantity and pack values or delete the row(s).`
      );
      return;
    }

    try {
      this.isLoading = true;

      // Define property order based on interface structure
      const propertyOrder = [
        'client_order',
        'order_details',
        'order_items',
        'order_notes',
        'order_files',
        'custom_fields'
      ];

      // Generate payload using PayloadGeneratorService
      const payload = this.payloadGeneratorService.generatePayload<ClientOrderCreatePayload | ClientOrderUpdatePayload>({
        formGroup: this.customerForm,
        propertyOrder: propertyOrder,
        fields: this.data as any,
        entityId: this.clientSOId || undefined,
        tableFormArrays: this.tableFormArrays,
        getFieldKey: (fieldName: string) => this.getFieldKey(fieldName),
        skipFields: this.clientSOId ? [] : ['order_no'], // Skip auto-generated fields on create
        fieldToInterfaceMap: CLIENT_ORDER_FIELD_TO_INTERFACE_MAP,
        tableToInterfaceMap: CLIENT_ORDER_TABLE_TO_INTERFACE_MAP,
        arrayKeys: ['order_items', 'order_notes', 'order_files'],
        dynamicFormTabs: this.dynamicFormTabs,
        extractObjectId: (value: any, fieldName: string) => {
          // Extract ID from autocomplete/select objects
          if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
            // Special handling for client_id
            if (fieldName === 'client_id') {
              return value.id ?? value.value ?? value.client_id ?? value;
            }
            // Special handling for item_id (from items table)
            if (fieldName === 'item_id' || fieldName === 'item') {
              return value.id ?? value.value ?? value.item_id ?? value;
            }

            // Special handling for place_of_supply_id
            if (fieldName === 'place_of_supply_id') {
              return value.id ?? value.value ?? value.state_id ?? value;
            }
            return value.id ?? value.value ?? value[fieldName] ?? value;
          }
          return value;
        },
        transformPayload: (generatedPayload: any) => {
          // Fix table mapping: map sub_tab_3 or items table to order_items
          // Check for any table keys that should be mapped to order_items

          // Remove item-level fields from client_order (these should only be in order_items)
          if (generatedPayload.client_order) {
            const itemFields = [
              'quantity', 'scheme', 'pack', 'foc', 'mrp', 'rate',
              'discount_percentage', 'discount_amount', 'gross_amount',
              'tax_amount', 'total_amount', 'client_code'
            ];
            itemFields.forEach(field => {
              if (generatedPayload.client_order.hasOwnProperty(field)) {
                delete generatedPayload.client_order[field];
              }
            });

            // Map ship_to to ship_to_id if present
            if (generatedPayload.client_order.ship_to !== undefined) {
              generatedPayload.client_order.ship_to_id = generatedPayload.client_order.ship_to;
              delete generatedPayload.client_order.ship_to;
            }
          }

          // Map bill_to to order_details.billing_address if present
          if (generatedPayload.order_details) {
            if (generatedPayload.order_details.bill_to !== undefined) {
              generatedPayload.order_details.billing_address = generatedPayload.order_details.bill_to;
              delete generatedPayload.order_details.bill_to;
            }
          } else if (generatedPayload.bill_to !== undefined) {
            // If bill_to is at root level, move it to order_details
            if (!generatedPayload.order_details) {
              generatedPayload.order_details = {};
            }
            generatedPayload.order_details.billing_address = generatedPayload.bill_to;
            delete generatedPayload.bill_to;
          }

          // Calculate totals from order_items
          const totals = this.calculateTotals();

          // Add totals to client_order
          if (generatedPayload.client_order) {
            generatedPayload.client_order.gross_amount = totals.grossAmount;
            generatedPayload.client_order.discount_amount = totals.discountAmount;
            generatedPayload.client_order.tax_amount = totals.taxAmount;
            generatedPayload.client_order.final_amount = totals.finalAmount;
          }

          // Handle selectedShippingAddressId and selectedBillingAddressId
          if (this.selectedShippingAddressId) {
            if (!generatedPayload.client_order) {
              generatedPayload.client_order = {};
            }
            generatedPayload.client_order.ship_to_id = this.selectedShippingAddressId;
          }

          if (this.selectedBillingAddressId) {
            if (!generatedPayload.order_details) {
              generatedPayload.order_details = {};
            }
            generatedPayload.order_details.billing_address = this.selectedBillingAddressId;
          }

          // Handle selectedLocationId
          if (this.selectedLocationId) {
            if (!generatedPayload.client_order) {
              generatedPayload.client_order = {};
            }
            generatedPayload.client_order.location = this.selectedLocationId;
          }

          // Map user_notes to order_notes if present
          if (generatedPayload.user_notes && !generatedPayload.order_notes) {
            generatedPayload.order_notes = generatedPayload.user_notes;
            delete generatedPayload.user_notes;
          }

          // Process order_items to extract item_id from item objects and item_name
          if (generatedPayload.order_items && Array.isArray(generatedPayload.order_items)) {
            generatedPayload.order_items = generatedPayload.order_items.map((item: any) => {
              const processedItem = { ...item };

              // Extract item_id from item object if present
              if (item.item && typeof item.item === 'object') {
                processedItem.item_id = item.item.id ?? item.item.value ?? item.item.item_id ?? processedItem.item_id;
                // Extract item_name from item object if not already set
                if (!processedItem.item_name && item.item.name) {
                  processedItem.item_name = item.item.name;
                } else if (!processedItem.item_name && item.item.item_name) {
                  processedItem.item_name = item.item.item_name;
                }
              }

              // Extract item_name from item_master_name if present
              if (!processedItem.item_name && item.item_master_name) {
                processedItem.item_name = item.item_master_name;
              }

              // Extract unit_id from units object if present
              if (item.units && typeof item.units === 'object') {
                processedItem.unit_id = item.units.id ?? item.units.value ?? item.units.unit_id ?? processedItem.unit_id;
                processedItem.unit_name = item.units.name ?? item.units.unit_name ?? processedItem.unit_name;
              }

              if (item.unit_id) {
                processedItem.unit_name = this.getUnitName(item.unit_id);
              }

              return processedItem;
            });
          }

          // Remove empty arrays
          if (generatedPayload.order_notes && Array.isArray(generatedPayload.order_notes) && generatedPayload.order_notes.length === 0) {
            delete generatedPayload.order_notes;
          }
          if (generatedPayload.order_files && Array.isArray(generatedPayload.order_files) && generatedPayload.order_files.length === 0) {
            delete generatedPayload.order_files;
          }
          if (generatedPayload.custom_fields && Object.keys(generatedPayload.custom_fields).length === 0) {
            delete generatedPayload.custom_fields;
          }

          return generatedPayload;
        }
      });

      // Remove all IDs from payload when duplicating
      let finalPayload = payload;
      if (this.mode === 'duplicate') {
        finalPayload = this.removeIdsForDuplicate(payload);
      }

      // Force create operation in duplicate mode
      const isCreate = this.mode === 'duplicate' ? true : !this.clientSOId;
      let response;
      if (isCreate) {
        response = await lastValueFrom(this.clientOrderService.createClientOrder(finalPayload as any));
      } else {
        response = await lastValueFrom(
          this.clientOrderService.updateClientOrder(String(this.clientSOId), finalPayload as any)
        );
      }

      // Update approval status from response if available
      if (response?.data?.approval_status) {
        this.approvalStatus = response.data.approval_status;
      }

      // Update SO status from response if available
      if (response?.data?.status) {
        this.soStatus = response.data.status;
        // Refresh page actions when status changes
        this.setupPageActions();
      }

      if (isCreate) {
        this.toasterService.showMessage(
          this.toasterService.MESSAGE_TYPES.SUCCESS,
          'Client order created successfully.'
        );
      } else {
        this.toasterService.showMessage(
          this.toasterService.MESSAGE_TYPES.SUCCESS,
          'Client order updated successfully.'
        );
      }
      if (response) {
        // Get the saved SO ID from response
        const savedSOId = response?.data?.id || this.clientSOId;
        if (savedSOId) {
          // Redirect to view mode of the saved SO instead of listing page
          // Use window.location.href to force a full reload since we're navigating to the same route
          const baseHref = this.getBaseHref();
          const urlTree = this.router.createUrlTree(['/clients/sales-order'], {
            queryParams: { soId: savedSOId, mode: 'view' }
          });
          const serializedUrl = this.router.serializeUrl(urlTree);
          const pathWithBase = baseHref ? `${baseHref}${serializedUrl}` : serializedUrl;
          window.location.href = pathWithBase;
        }
      }
    } catch (error: any) {
      // Handle PayloadValidationError
      if (error instanceof PayloadValidationError || error.name === 'PayloadValidationError') {
        const missingFields = error.missingFields || [];
        const errorMessage = missingFields.length > 0
          ? `Please fill in the following required fields: ${missingFields.join(', ')}`
          : 'Please fill in all required fields.';
        this.toasterService.showMessage(this.toasterService.MESSAGE_TYPES.ERROR, errorMessage);
        return;
      }

      // Handle other errors
      const isCreate = !this.clientSOId;
      const message =
        error?.error?.error ??
        error?.error?.message ??
        error?.message ??
        (isCreate
          ? 'Failed to create client order. Please try again.'
          : 'Failed to update client order. Please try again.');
      this.toasterService.showMessage(this.toasterService.MESSAGE_TYPES.ERROR, message);
    } finally {
      this.isLoading = false;
    }
  }

  onTableFieldChanged(event: any, subTab: any) {
    if (!event || !event.key || event.rowIndex === undefined || event.rowIndex === null) {
      return;
    }

    // Handle is_deleted changes - update summary when row is deleted/undeleted
    if (event.fieldName === 'is_deleted') {
      this.updateItemsSummary();
      return;
    }

    if (event.fieldName === 'item_name') {
      this.onItemTableFieldChanged(event);
    }

    const tableKey: any = (subTab?.sub_tab_key || subTab?.key || '').toString().toLowerCase();
    if (Array.isArray(event.value) && this.calculatedTaxType) {
      const formArray = this.tableFormArrays.get(event.key) as FormArray;
      if (formArray) {
        formArray.controls.forEach((control: any, index: number) => {
          if (control instanceof FormGroup) {
            const taxTypeControl = control.get(this.getFieldKey('tax_type'));
            if (Object.keys(event.value[index]).includes('tax_type')) {
              if (taxTypeControl) {
                taxTypeControl.patchValue(this.calculatedTaxType, { emitEvent: false });
              } else {
                control.addControl(this.getFieldKey('tax_type'), this.fb.control(this.calculatedTaxType));
              }
            }
          }
        });
      }
    }
    if (tableKey === 'items') {
      this.calculateItemsTable(event);
    } else if (tableKey === 'services') {
      this.calculateItemsTable(event);
    }
    this.updateItemsSummary();
  }

  private onItemTableFieldChanged(event: any) {
    const tableArray = this.tableFormArrays.get(event.key);
    if (!tableArray || !(tableArray instanceof FormArray)) {
      return;
    }
    const row = tableArray.at(event.rowIndex);
    if (!(row instanceof FormGroup)) {
      return;
    }

    const selection = event?.fieldValue?.value;
    if (!selection) {
      // Clear all item-related fields if no selection
      this.patchRowValue(row, 'description', '');
      this.patchRowValue(row, 'tax_rate', null);
      this.patchRowValue(row, 'item_name', '');
      this.patchRowValue(row, 'hsn_code', '');
      this.patchRowValue(row, 'mrp', null);
      this.patchRowValue(row, 'pack', null);
      this.patchRowValue(row, 'unit_id', null);
      this.patchRowValue(row, 'item_id', null);
      this.patchRowValue(row, 'discount_percentage', null);
      this.patchRowValue(row, 'uqc', null);
      this.patchRowValue(row, 'rate', null);
      this.patchRowValue(row, 'current_stock', null);
      this.patchRowValue(row, 'last_purchase_price', null);

      // Re-enable pack and units fields when item is deselected
      const packKey = this.getFieldKey('pack');
      const unitsKey = this.getFieldKey('units');
      const packControl = row.get(packKey);
      const unitsControl = row.get(unitsKey);
      if (packControl) {
        packControl.enable({ emitEvent: false });
      }
      if (unitsControl) {
        unitsControl.enable({ emitEvent: false });
      }
      return;
    }
    const description = selection?.item_master_description ?? selection?.description ?? '';
    this.patchRowValue(row, 'description', description);

    // Tax rate
    const tax_rate = selection?.tax_rate ?? selection?.gst_percentage ?? null;
    this.patchRowValue(row, 'tax_rate', tax_rate);

    // Item name
    const name = selection?.item_master_name ?? selection?.name ?? selection?.label ?? '';
    this.patchRowValue(row, 'item_name', name);

    // HSN code
    const hsn = selection?.hsn_code ?? selection?.hsn ?? '';
    this.patchRowValue(row, 'hsn_code', hsn);

    // Pack (from packing_unit)
    const packingUnit = selection?.packing_unit ?? null;
    this.patchRowValue(row, 'pack', packingUnit);

    // MRP
    const mrp = (selection?.mrp ?? 0) / (packingUnit ?? 1);
    this.patchRowValue(row, 'mrp', mrp);

    // Rate - try multiple possible field names
    const rate = (selection?.rate ?? 0) / (packingUnit ?? 1);
    if (rate !== null && rate !== undefined) {
      this.patchRowValue(row, 'rate', rate);
    }

    // Discount Percentage (from discount_percent)
    const discountPercent = selection?.discount_percent ?? null;
    this.patchRowValue(row, 'discount_percentage', discountPercent);

    // Units (from packaging_id)
    const packagingId = selection?.packaging_id ?? null;
    this.patchRowValue(row, 'unit_id', packagingId);

    // UQC (Unit Quantity Code) - prefill both uqc and india_tax_uqc if available
    const uqc = selection?.uqc ?? null;
    if (uqc) {
      this.patchRowValue(row, 'uqc', uqc);
    }

    // Last Purchase Price (from recent_rate)
    const lastPurchasePrice = selection?.recent_rate ?? null;
    if (lastPurchasePrice !== null && lastPurchasePrice !== undefined) {
      this.patchRowValue(row, 'last_purchase_price', this.toNumberValue(lastPurchasePrice));
    }

    // Item ID
    const itemId = selection.item_id ?? selection.value ?? selection.code ?? selection.id ?? null;
    if (itemId) {
      const itemIdKey = this.getFieldKey('item_id');
      const existingControl = row.get(itemIdKey);
      if (!existingControl) {
        row.addControl(itemIdKey, this.fb.control(itemId));
      }
      this.patchRowValue(row, 'item_id', itemId);
    } else {
      this.patchRowValue(row, 'item_id', null);
    }

    // Item Category ID
    const categoryId = selection?.category_id ?? null;
    if (categoryId !== null && categoryId !== undefined) {
      const categoryIdKey = this.getFieldKey('item_category_id');
      const existingControl = row.get(categoryIdKey);
      if (!existingControl) {
        row.addControl(categoryIdKey, this.fb.control(categoryId));
      }
      this.patchRowValue(row, 'item_category_id', categoryId);
    } else {
      this.patchRowValue(row, 'item_category_id', null);
    }

    // Disable/enable pack and units fields based on item_category_id
    const isService = categoryId === 2;

    // Current Stock (from total_current_quantity)
    const currentStock = selection?.total_current_quantity ?? null;
    if (currentStock !== null && currentStock !== undefined && !isService) {
      this.patchRowValue(row, 'current_stock', this.toNumberValue(currentStock));
    }

    // Apply field state based on category
    this.applyItemCategoryFieldState(row, categoryId);
  }

  /**
   * Apply pack/units/scheme field state based on item_category_id
   * Used both during initial load and when item is selected
   */
  private applyItemCategoryFieldState(row: FormGroup, categoryId: any): void {
    const isService = categoryId === 2 || categoryId === '2' || Number(categoryId) === 2;

    const packControl = row.get(this.getFieldKey('pack'));
    const unitsControl = row.get(this.getFieldKey('units')) || row.get(this.getFieldKey('unit_id'));
    const schemeControl = row.get(this.getFieldKey('scheme'));
    const mrpControl = row.get(this.getFieldKey('mrp'));
    const rateControl = row.get(this.getFieldKey('rate'));

    if (packControl) {
      if (isService) {
        packControl.patchValue(1, { emitEvent: false });
        packControl.disable({ emitEvent: false });
      } else {
        packControl.enable({ emitEvent: false });
      }
    }

    if (unitsControl) {
      if (isService) {
        unitsControl.patchValue(null, { emitEvent: false });
        unitsControl.disable({ emitEvent: false });
      } else {
        unitsControl.enable({ emitEvent: false });
      }
    }

    if (isService) {
      if (schemeControl) {
        schemeControl.patchValue(null, { emitEvent: false });
        schemeControl.disable({ emitEvent: false });
      }
      // For service items, force MRP to 0 and disable it, and ensure rate is enabled
      if (mrpControl) {
        mrpControl.patchValue(0, { emitEvent: false });
        mrpControl.disable({ emitEvent: false });
      }
      if (rateControl) {
        rateControl.enable({ emitEvent: false });
      }
    } else {
      if (schemeControl) {
        schemeControl.enable({ emitEvent: false });
      }
      // For non-service items, re-enable MRP so it can be edited if needed
      if (mrpControl) {
        mrpControl.enable({ emitEvent: false });
      }
    }
  }

  private patchRowValue(row: FormGroup, fieldName: string, value: any) {
    const control = row.get(this.getFieldKey(fieldName));
    if (!control) {
      return;
    }

    const currentValue = control.value;
    if (this.areValuesEqual(currentValue, value)) {
      return;
    }

    control.patchValue(value, { emitEvent: false });
  }

  private areValuesEqual(a: any, b: any): boolean {
    if (a === b) {
      return true;
    }

    if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime();
    }

    if (typeof a === 'number' && typeof b === 'number') {
      return Math.abs(a - b) < 0.0001;
    }

    return false;
  }


  /**
 * Calculate values for items table
 * Handles: amount, discount_amount, tax_amount, total_amount calculations
 */
  private calculateItemsTable(event: any) {
    const tableKey = event.key;
    const rowIndex = event.rowIndex;
    const fieldName = event.fieldName;
    const tableArray = this.tableFormArrays.get(tableKey) as FormArray;

    if (!tableArray || !tableArray.controls[rowIndex]) return;
    const rowControl = tableArray.controls[rowIndex] as FormGroup;
    if (!rowControl) return;
    // Get current row values
    const quantity = Number(rowControl.get(this.getFieldKey('quantity'))?.value || 0);

    // Detect if this row is a service item using item_category_id/item_type
    const categoryIdControl = rowControl.get(this.getFieldKey('item_category_id'));
    const categoryIdValue = categoryIdControl?.value;
    const isService = categoryIdValue === 2 || categoryIdValue === '2' || Number(categoryIdValue) === 2;

    const mrpControl = rowControl.get(this.getFieldKey('mrp'));
    const rateControl = rowControl.get(this.getFieldKey('rate'));
    const mrp = Number(mrpControl?.value || 0);
    const rate = Number(rateControl?.value || 0);

    // For service items, use rate as the unit price and force MRP to 0 and disabled
    if (isService) {
      if (mrpControl) {
        mrpControl.patchValue(0, { emitEvent: false });
        mrpControl.disable({ emitEvent: false });
      }
      if (rateControl) {
        rateControl.enable({ emitEvent: false });
      }
    }

    const unitPrice = isService ? rate : mrp;
    const discountPercentage = Number(rowControl.get(this.getFieldKey('discount_percentage'))?.value || 0);
    const existingDiscountAmount = Number(rowControl.get(this.getFieldKey('discount_amount'))?.value || 0);
    const taxRate = Number(rowControl.get(this.getFieldKey('tax_rate'))?.value || 0);

    // Get row level flags
    const foc = rowControl.get(this.getFieldKey('foc'))?.value === true ||
      rowControl.get(this.getFieldKey('foc'))?.value === 1 ||
      rowControl.get(this.getFieldKey('foc'))?.value === 'true';

    // If FOC (Free of Charge) is true, set all amounts to 0
    let amount = 0;
    if (!foc) {
      // Calculate amount = quantity * unitPrice (only if not FOC)
      amount = Math.round((quantity * unitPrice) * 100) / 100;
    }

    // Handle bidirectional discount calculation (skip if FOC)
    let discountAmount = 0;
    let calculatedDiscountPercentage = 0;

    if (foc) {
      // If FOC, all amounts are 0
      discountAmount = 0;
      calculatedDiscountPercentage = 0;
    } else {
      if (fieldName === 'discount_percentage') {
        // If discount_percentage was changed, calculate discount_amount
        if (discountPercentage > 0 && amount > 0) {
          discountAmount = Math.round((amount * discountPercentage / 100) * 100) / 100;
          calculatedDiscountPercentage = discountPercentage;
        } else {
          discountAmount = 0;
          calculatedDiscountPercentage = 0;
        }
      } else if (fieldName === 'discount_amount') {
        // If discount_amount was changed, calculate discount_percentage
        if (existingDiscountAmount > 0 && amount > 0) {
          discountAmount = existingDiscountAmount;
          calculatedDiscountPercentage = Math.round((discountAmount / amount * 100) * 100) / 100;
        } else {
          discountAmount = 0;
          calculatedDiscountPercentage = 0;
        }
      } else {
        // If neither was changed, use existing values and calculate the other if needed
        // Prefer stored discount amount over recalculating from percentage to avoid rounding errors
        if (existingDiscountAmount > 0 && amount > 0) {
          discountAmount = existingDiscountAmount;
          calculatedDiscountPercentage = Math.round((discountAmount / amount * 100) * 100) / 100;
        } else if (discountPercentage > 0 && amount > 0) {
          discountAmount = Math.round((amount * discountPercentage / 100) * 100) / 100;
          calculatedDiscountPercentage = discountPercentage;
        } else if (discountPercentage > 0 && amount === 0) {
          // Preserve discount_percentage even if amount is 0 (e.g., when just set from item selection)
          // Don't calculate discount_amount until amount is available
          discountAmount = 0;
          calculatedDiscountPercentage = discountPercentage;
        } else {
          discountAmount = 0;
          calculatedDiscountPercentage = 0;
        }
      }
    }

    // Calculate net_amount = amount - discount_amount
    // Note: Since MRP is tax-inclusive, net_amount already includes the tax
    const netAmount = Math.round((amount - discountAmount) * 100) / 100;

    // Check if GST exempt is enabled at form level
    const gstExemptControl = this.customerForm?.get(this.getFieldKey('gst_exempt'));
    const isGstExempt = this.normalizeBoolean(gstExemptControl?.value);
    // - For products (category 1): MRP is GST-inclusive, back-calculate tax
    // - For services (category 2): Rate is GST-exclusive, add tax on top
    const existingTaxAmount = Number(rowControl.get(this.getFieldKey('tax_amount'))?.value || 0);
    let taxAmount = 0;
    let totalAmountBeforeTds = 0;

    if (foc || isGstExempt) {
      // If FOC or GST exempt, tax_amount is 0
      taxAmount = 0;
      totalAmountBeforeTds = Math.round(netAmount * 100) / 100;
    } else if (isService) {
      // Service items (category 2): Rate is GST-exclusive, add tax on top
      // Formula: taxAmount = netAmount * taxRate / 100
      if (fieldName === 'tax_rate') {
        if (taxRate > 0) {
          taxAmount = Math.round(((netAmount * taxRate) / 100) * 100) / 100;
        } else {
          taxAmount = 0;
        }
      } else if (taxRate > 0 && fieldName !== 'tax_amount') {
        taxAmount = Math.round(((netAmount * taxRate) / 100) * 100) / 100;
      } else if (existingTaxAmount > 0 && fieldName === 'tax_amount') {
        taxAmount = existingTaxAmount;
      } else {
        taxAmount = existingTaxAmount;
      }
      // For exclusive GST, total = net + tax
      totalAmountBeforeTds = Math.round((netAmount + taxAmount) * 100) / 100;
    } else {
      // Product items (category 1): MRP is GST-inclusive, back-calculate tax
      // Formula: taxAmount = netAmount * taxRate / (100 + taxRate)
      if (fieldName === 'tax_rate') {
        if (taxRate > 0) {
          taxAmount = Math.round(((netAmount * taxRate) / (100 + taxRate)) * 100) / 100;
        } else {
          taxAmount = 0;
        }
      } else if (taxRate > 0 && fieldName !== 'tax_amount') {
        taxAmount = Math.round(((netAmount * taxRate) / (100 + taxRate)) * 100) / 100;
      } else if (existingTaxAmount > 0 && fieldName === 'tax_amount') {
        taxAmount = existingTaxAmount;
      } else {
        taxAmount = existingTaxAmount;
      }
      // For inclusive GST, total = net (MRP already includes tax)
      totalAmountBeforeTds = Math.round(netAmount * 100) / 100;
    }

    // Calculate TDS amount based on tds_rate and gross_amount (amount before discount)
    // TDS is calculated on gross_amount, not total_amount
    const tdsRate = Number(rowControl.get(this.getFieldKey('tds_rate'))?.value || 0);
    const tdsAmountControl = rowControl.get(this.getFieldKey('tds_amount'));
    const existingTdsAmountValue = tdsAmountControl?.value;
    const existingTdsAmount = existingTdsAmountValue !== null && existingTdsAmountValue !== undefined
      ? Number(existingTdsAmountValue)
      : null;
    let tdsAmount = 0;

    // TDS is only calculated if not FOC is true (or not specified, default to true)
    // Use amount (gross_amount before discount) for TDS calculation

    // If TDS shouldn't be calculated, preserve existing value if it was prefilled, otherwise set to 0
    if (fieldName === 'initial_load' && existingTdsAmount !== null && existingTdsAmount !== undefined) {
      tdsAmount = existingTdsAmount;
    } else {
      tdsAmount = 0;
    }


    // Calculate final total_amount = net_amount + tax_amount - tds_amount
    const totalAmount = Math.round((totalAmountBeforeTds - tdsAmount) * 100) / 100;

    // Update form controls if they exist
    const amountKey = this.getFieldKey('gross_amount');
    const discountAmountKey = this.getFieldKey('discount_amount');
    const discountPercentageKey = this.getFieldKey('discount_percentage');
    const taxAmountKey = this.getFieldKey('tax_amount');
    const totalAmountKey = this.getFieldKey('total_amount');
    const tdsRateKey = this.getFieldKey('tds_rate');
    const tdsAmountKey = this.getFieldKey('tds_amount');
    const tdsSectionKey = this.getFieldKey('tds_section');

    // Handle FOC changes - if FOC is set to true, reset all amounts and disable calculation fields
    // Also handle initial_load to ensure fields are disabled if FOC is already true
    if (fieldName === 'foc' || fieldName === 'initial_load') {
      // Toggle calculation fields based on FOC status
      this.toggleCalculationFieldsForFoc(rowControl, foc);

      if (foc) {
        // If FOC is true, set all amounts to 0
        if (rowControl.get(amountKey)) {
          rowControl.patchValue({ [amountKey]: 0 }, { emitEvent: false });
        }
        if (rowControl.get(discountAmountKey)) {
          rowControl.patchValue({ [discountAmountKey]: 0 }, { emitEvent: false });
        }
        if (rowControl.get(discountPercentageKey)) {
          rowControl.patchValue({ [discountPercentageKey]: 0 }, { emitEvent: false });
        }
        if (rowControl.get(taxAmountKey)) {
          rowControl.patchValue({ [taxAmountKey]: 0 }, { emitEvent: false });
        }
        if (rowControl.get(totalAmountKey)) {
          rowControl.patchValue({ [totalAmountKey]: 0 }, { emitEvent: false });
        }
        if (rowControl.get(tdsAmountKey)) {
          rowControl.patchValue({ [tdsAmountKey]: 0 }, { emitEvent: false });
        }
        // Also set mrp to 0 if FOC
        const mrpKey = this.getFieldKey('mrp');
        if (rowControl.get(mrpKey)) {
          rowControl.patchValue({ [mrpKey]: 0 }, { emitEvent: false });
        }
      }
    }

    // Only update if field changed is not the calculated field itself (to avoid infinite loops)
    // For products (category 1): gross_amount = total_amount - tax_amount (pre-tax value)
    // For services (category 2): gross_amount = netAmount (the rate is already pre-tax)
    if (fieldName !== 'gross_amount' && fieldName !== 'amount' && rowControl.get(amountKey)) {
      const grossAmountValue = isService ? netAmount : (totalAmountBeforeTds - taxAmount);
      rowControl.patchValue({ [amountKey]: grossAmountValue }, { emitEvent: false });
    }

    // Update discount_amount and discount_percentage bidirectionally
    if (fieldName !== 'discount_amount' && rowControl.get(discountAmountKey)) {
      rowControl.patchValue({ [discountAmountKey]: discountAmount }, { emitEvent: false });
    }
    if (fieldName !== 'discount_percentage' && rowControl.get(discountPercentageKey)) {
      rowControl.patchValue({ [discountPercentageKey]: calculatedDiscountPercentage }, { emitEvent: false });
    }

    // Update tax_amount (will be 0 if FOC)
    if (fieldName !== 'tax_amount' && rowControl.get(taxAmountKey)) {
      rowControl.patchValue({ [taxAmountKey]: taxAmount }, { emitEvent: false });
    }

    if (fieldName !== 'total_amount' && rowControl.get(totalAmountKey)) {
      rowControl.patchValue({ [totalAmountKey]: totalAmount }, { emitEvent: false });
    }

    // Update tds_amount
    if (fieldName !== 'tds_amount' && rowControl.get(tdsAmountKey)) {
      rowControl.patchValue({ [tdsAmountKey]: tdsAmount }, { emitEvent: false });
    }

    // Note: updateItemsSummary is called in onTableFieldChanged to avoid duplicate calls
  }

  /**
   * Disable/enable calculation fields based on FOC status
   */
  private toggleCalculationFieldsForFoc(rowControl: FormGroup, foc: boolean) {
    const calculationFields = [
      'mrp',
      'last_purchase_price',
      'discount_percentage',
      'discount_amount',
      'tax_rate',
      'tax_amount',
      'tds_rate',
      'tds_amount',
      'tds_section'
    ];

    calculationFields.forEach(fieldName => {
      const fieldKey = this.getFieldKey(fieldName);
      const control = rowControl.get(fieldKey);
      if (control) {
        if (foc) {
          control.disable({ emitEvent: false });
        } else {
          if (fieldName === 'mrp' && rowControl.get(this.getFieldKey('item_category_id'))?.value == 2) {
            return;
          }
          control.enable({ emitEvent: false });
        }
      }
    });
  }

  private normalizeBoolean(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') {
        return false;
      }
      const normalized = trimmed.toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }
    return false;
  }

  private setFieldOptions(fieldName: string, options: any[]): void {
    const normalizedOptions = Array.isArray(options) ? options : [];
    const fields = [...(this.primaryFields ?? []), ...(this.data ?? [])];

    fields
      .filter(field => field.field_name === fieldName)
      .forEach(field => {
        field.options = normalizedOptions;
      });

    if (fieldName === 'ship_to' && normalizedOptions.length === 0) {
      this.selectedShippingAddressId = null;
    }

    if (fieldName === 'bill_to' && normalizedOptions.length === 0) {
      this.selectedBillingAddressId = null;
    }
  }

  private getFieldOptions(fieldName: string): any[] {
    const fields = [...(this.primaryFields ?? []), ...(this.data ?? [])];
    for (const field of fields) {
      if (field.field_name === fieldName && Array.isArray(field.options)) {
        return field.options;
      }
    }
    return [];
  }

  private normalizeOptionValue(value: any): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'object') {
      const candidate =
        value.id ?? value.value ?? value.client_id ?? value.name ?? value.label ?? null;
      return candidate !== null && candidate !== undefined ? String(candidate) : null;
    }

    return String(value);
  }

  private findFieldOption(fieldName: string, value: any): any | null {
    const normalizedValue = this.normalizeOptionValue(value);
    if (normalizedValue === null) {
      return null;
    }

    const options = this.getFieldOptions(fieldName);
    return (
      options.find(option => {
        const candidate =
          option?.value ?? option?.id ?? option?.client_id ?? option?.name ?? option?.label;
        return candidate !== undefined && candidate !== null
          ? String(candidate) === normalizedValue
          : false;
      }) ?? null
    );
  }

  private handleShipToSelection(selection: any): void {
    // Only store the ID (bigint converted to string) from the selection
    this.selectedShippingAddressId = this.normalizeIdValue(selection);
  }

  private handleBillToSelection(selection: any): void {
    // Only store the ID (bigint converted to string) from the selection
    this.selectedBillingAddressId = this.normalizeIdValue(selection?.id ?? null);
  }



  private calculateTotals(): {
    grossAmount: number;
    baseAmount: number;
    discountAmount: number;
    taxAmount: number;
    finalAmount: number;
  } {
    const itemsConfig = this.getItemsTableConfig();
    const itemsArray = itemsConfig ? this.getTableArray(itemsConfig.key) : null;

    if (!itemsArray || itemsArray.length === 0) {
      return {
        grossAmount: 0,
        baseAmount: 0,
        discountAmount: 0,
        taxAmount: 0,
        finalAmount: 0
      };
    }

    let grossAmount = 0;
    let baseAmount = 0;
    let discountAmount = 0;
    let taxAmount = 0;

    const grossAmountKey = this.getFieldKey('gross_amount');
    const amountKey = this.getFieldKey('amount');
    const discountAmountKey = this.getFieldKey('discount_amount');
    const taxAmountKey = this.getFieldKey('tax_amount');

    const itemCategoryIdKey = this.getFieldKey('item_category_id');
    const totalAmountKey = this.getFieldKey('total_amount');

    itemsArray.controls.forEach(control => {
      if (!(control instanceof FormGroup)) {
        return;
      }

      if (this.isRowMarkedDeleted(control)) {
        return;
      }

      const rowGrossAmount =
        Number(control.get(grossAmountKey)?.value ?? control.get(amountKey)?.value ?? 0);
      const rowDiscountAmount = Number(control.get(discountAmountKey)?.value ?? 0);
      const rowTaxAmount = Number(control.get(taxAmountKey)?.value ?? 0);
      const rowTotalAmount = Number(control.get(totalAmountKey)?.value ?? 0);
      
      // gross_amount in the row is now already the pre-tax value for both products and services
      // So baseAmount = grossAmount directly
      const rowBaseAmount = rowGrossAmount;

      grossAmount += isNaN(rowGrossAmount) ? 0 : rowGrossAmount;
      baseAmount += isNaN(rowBaseAmount) ? 0 : rowBaseAmount;
      discountAmount += isNaN(rowDiscountAmount) ? 0 : rowDiscountAmount;
      taxAmount += isNaN(rowTaxAmount) ? 0 : rowTaxAmount;
    });

    grossAmount = Math.round(grossAmount * 100) / 100;
    baseAmount = Math.round(baseAmount * 100) / 100;
    discountAmount = Math.round(discountAmount * 100) / 100;
    taxAmount = Math.round(taxAmount * 100) / 100;

    // Calculate final amount by summing total_amount from each row
    let finalAmount = 0;
    itemsArray.controls.forEach(control => {
      if (!(control instanceof FormGroup)) {
        return;
      }
      if (this.isRowMarkedDeleted(control)) {
        return;
      }
      finalAmount += Number(control.get(totalAmountKey)?.value ?? 0);
    });
    finalAmount = Math.round(finalAmount * 100) / 100;

    return {
      grossAmount,
      baseAmount,
      discountAmount,
      taxAmount,
      finalAmount
    };
  }

  private async loadClients(): Promise<void> {
    try {
      this.ensureClientAutocompleteInitialized();
      // Set empty options initially - will be populated by search
      this.clientOptions = [];
      this.applyClientOptionsToField();
    } catch (error) {
      console.error('Error initializing client field:', error);
      this.clientOptions = [];
    }
  }

  private async loadFormTemplates(): Promise<void> {
    try {
      const response = await lastValueFrom(this.templateService.getSOFormTemplates());
      const templates = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      this.templateOptions = templates;

      const defaultTemplate =
        templates.find((tpl: any) => tpl?.is_default) ?? templates[0] ?? null;
      if (!this.currentTemplateId && defaultTemplate) {
        this.currentTemplateId = this.normalizeIdValue(
          defaultTemplate.id ?? defaultTemplate.template_id ?? defaultTemplate.value
        );
      }
    } catch (error) {
      console.error('Error loading client order form templates:', error);
      this.templateOptions = [];
    }
  }

  private async loadMasters(): Promise<void> {
    try {
      const mastersToLoad = [
        'tax-types',
        'tax-rates',
        'registration-type-options',
        'terms',
        'shipping-methods',
        'shipping-carriers',
        'note-types',
        'directions',
        'line-of-businesses',
        'registration-types',
        'states',
        'file-types',
        'projects',
        'product-categories',
        'nature-of-goods',
        'products',
        'client-categories',
      ];

      const response = await lastValueFrom(
        this.mastersService.getMasters(mastersToLoad)
      );

      const mastersData = response?.data ?? response ?? {};

      // Store master data
      if (mastersData['tax-types']) {
        this.masterData['tax-types'] = Array.isArray(mastersData['tax-types'])
          ? mastersData['tax-types']
          : [];
      }
      if (mastersData['tax-rates']) {
        this.masterData['tax-rates'] = Array.isArray(mastersData['tax-rates'])
          ? mastersData['tax-rates']
          : [];
      }
      if (mastersData['registration-type-options']) {
        this.masterData['registration-type-options'] = Array.isArray(mastersData['registration-type-options'])
          ? mastersData['registration-type-options']
          : [];
      }
      if (mastersData['terms']) {
        this.masterData['terms'] = Array.isArray(mastersData['terms'])
          ? mastersData['terms']
          : [];
      }
      if (mastersData['shipping-methods']) {
        this.masterData['shipping-methods'] = Array.isArray(mastersData['shipping-methods'])
          ? mastersData['shipping-methods']
          : [];
      }
      if (mastersData['shipping-carriers']) {
        this.masterData['shipping-carriers'] = Array.isArray(mastersData['shipping-carriers'])
          ? mastersData['shipping-carriers']
          : [];
      }
      if (mastersData['note-types']) {// TODO make the notes type here
        this.masterData['note-types'] = Array.isArray(mastersData['note-types'])
          ? mastersData['note-types']
          : [];
      }
      if (mastersData['directions']) {
        this.masterData['directions'] = Array.isArray(mastersData['directions'])
          ? mastersData['directions']
          : [];
      }
      if (mastersData['line-of-businesses']) {
        this.masterData['line-of-businesses'] = Array.isArray(mastersData['line-of-businesses'])
          ? mastersData['line-of-businesses']
          : [];
        // populating lineOfBusinessMap
        this.masterData['line-of-businesses'].forEach((item: any) => {
          const id = item.id || item.value;
          const name = item.name || '';
          if (id) {
            this.lineOfBusinessMap[id] = name;
          }
        });
      }
      if (mastersData['registration-types']) {
        this.masterData['registration-types'] = Array.isArray(mastersData['registration-types'])
          ? mastersData['registration-types']
          : [];
      }
      if (mastersData['file-types']) {
        this.masterData['file-types'] = Array.isArray(mastersData['file-types'])
          ? mastersData['file-types']
          : [];
      }
      if (mastersData['projects']) {
        this.masterData['projects'] = Array.isArray(mastersData['projects'])
          ? mastersData['projects']
          : [];
      }
      if (mastersData['product-categories']) {
        this.masterData['product-categories'] = Array.isArray(mastersData['product-categories'])
          ? mastersData['product-categories']
          : [];
      }
      if (mastersData['nature-of-goods']) {
        this.masterData['nature-of-goods'] = Array.isArray(mastersData['nature-of-goods'])
          ? mastersData['nature-of-goods']
          : [];
      }
      if (mastersData['products']) {
        this.masterData['products'] = Array.isArray(mastersData['products'])
          ? mastersData['products']
          : [];
      }
      // Load states for place_of_supply_id
      if (mastersData['states']) {
        const states = Array.isArray(mastersData['states'])
          ? mastersData['states']
          : [];
        this.stateOptions = states.map((item: any) => ({
          ...item,
          id: String(item.state_code),
          name: item.state_code ? `${item.state_code}-${item.state_name}` : item.state_name,
          value: String(item.state_code || item.state_id), // Store state_id as value
          label: item.state_code ? `${item.state_code}-${item.state_name}` : item.state_name,
          state_code: String(item.state_code),
          state_id: String(item.state_id ?? item.id ?? ''),
          country_code: String(item.country_code),
        }));
        
        // Update place_of_supply_id field options with all state options
        const placeOfSupplyFields = this.data.filter((f: any) => f.field_name === 'place_of_supply_id');
        placeOfSupplyFields.forEach((field: any) => {
          if (['select', 'search'].includes(field.field_type)) {
            if (Array.isArray(this.stateOptions) && this.stateOptions.length > 0) {
              field.options = this.normalizeOptions(this.stateOptions);
            }
          }
        });
        
        // Also set using setFieldOptions
        if (this.stateOptions.length > 0) {
          this.setFieldOptions('place_of_supply_id', this.normalizeOptions(this.stateOptions));
          this.setFieldOptions('place_of_supply', this.normalizeOptions(this.stateOptions));
        }
      }

      // Load sales types
      if (mastersData['client-categories']) {
        const salesTypes = Array.isArray(mastersData['client-categories'])
          ? mastersData['client-categories']
          : [];
        this.salesTypeOptions = salesTypes.map((item: any) => ({
          ...item,
          id: item.id || item.value,
          name: item.name || item.label || item.sales_type_name || '',
          value: item.id || item.value,
          label: item.name || item.label || item.sales_type_name || '',
        }));
        // Note: Field options will be set in bindMastersToFields() which is called after this.data is populated
      }

    } catch (error) {
      console.error('Error loading masters:', error);
    }
  }

  private async loadPackagingMasters(): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.mastersService.getPackagingMasters({ limit: 200, is_deleted: false })
      );

      const packagingMasters = response?.data?.packaging_masters || response?.data || [];

      this.unitsOptions = Array.isArray(packagingMasters)
        ? packagingMasters.map((item: any) => ({
          ...item,
          id: item.id || item._id || item.uuid,
          name: item.name || item.display_name || '',
          value: item.id || item._id || item.uuid,
          label: item.name || item.display_name || '',
          code: item.code || item.id || item._id || item.uuid,
        }))
        : [];
    } catch (error) {
      console.error('Error loading packaging masters:', error);
      this.unitsOptions = [];
    }
  }

  private normalizeUnitsOptions(options: any[]): any[] {
    if (!Array.isArray(options) || options.length === 0) {
      return [];
    }

    return options.map((opt: any) => {
      const label = opt.label ?? opt.name ?? opt.display_name ?? String(opt.id ?? opt.value ?? '');
      const value = opt.value ?? opt.id ?? opt.code ?? label;
      return {
        ...opt,
        id: value,
        value: value,
        label: label,
        name: label,
        raw: opt
      };
    });
  }

  private bindMastersToFields(): void {
    if (!this.data || this.data.length === 0) {
      return;
    }

    // Bind tax-types to tax_type field
    const taxTypeField = this.data.find(
      (field: FieldConfig) => field.field_name === 'tax_type'
    );
    if (taxTypeField && this.masterData['tax-types']) {
      taxTypeField.options = this.transformMasterDataToOptions(
        this.masterData['tax-types'],
        'tax_type'
      );
    }

    // Bind tax-rates to tax_rate field
    const taxRateField = this.data.find(
      (field: FieldConfig) => field.field_name === 'tax_rate'
    );
    if (taxRateField && this.masterData['tax-rates']) {
      taxRateField.options = this.transformMasterDataToOptions(
        this.masterData['tax-rates'],
        'tax_rate'
      );
    }

    // Bind registration-type-options to registration_type field
    const registrationTypeField = this.data.find(
      (field: FieldConfig) => field.field_name === 'registration_type'
    );
    if (registrationTypeField && this.masterData['registration-type-options']) {
      registrationTypeField.options = this.transformMasterDataToOptions(
        this.masterData['registration-type-options'],
        'registration_type'
      );
    }

    // Bind terms to terms field
    const termsField = this.data.find(
      (field: FieldConfig) => field.field_name === 'terms'
    );
    if (termsField && this.masterData['terms']) {
      termsField.options = this.transformMasterDataToOptions(
        this.masterData['terms'],
        'terms'
      );
    }

    // Bind shipping-methods to shipping_method field
    const shippingMethodField = this.data.find(
      (field: FieldConfig) => field.field_name === 'shipping_method'
    );
    if (shippingMethodField && this.masterData['shipping-methods']) {
      shippingMethodField.options = this.transformMasterDataToOptions(
        this.masterData['shipping-methods'],
        'shipping_method'
      );
    }

    // Bind shipping-carriers to shipping_carrier field
    const shippingCarriersField = this.data.find(
      (field: FieldConfig) => field.field_name === 'shipping_carrier'
    );
    if (shippingCarriersField && this.masterData['shipping-carriers']) {
      shippingCarriersField.options = this.transformMasterDataToOptions(
        this.masterData['shipping-carriers'],
        'shipping_carrier'
      );
    }
    const typesField = this.data.find(
      (field: FieldConfig) => field.field_name === 'type'
    );
    if (typesField && this.masterData['note-types']) {
      typesField.options = this.transformMasterDataToOptions(
        this.masterData['note-types'],
        'type'
      );
    }
    const directionsField = this.data.find(
      (field: FieldConfig) => field.field_name === 'direction'
    );
    if (directionsField && this.masterData['directions']) {
      directionsField.options = this.transformMasterDataToOptions(
        this.masterData['directions'],
        'direction'
      );
    }

    // Bind line-of-businesses to line_of_business field (in items table)
    const lineOfBusinessField = this.data.find(
      (field: FieldConfig) => field.field_name === 'line_of_business_id'
    );
    if (lineOfBusinessField && this.masterData['line-of-businesses']) {
      lineOfBusinessField.options = this.transformMasterDataToOptions(
        this.masterData['line-of-businesses'],
        'line_of_business_id'
      );
    }
    const projectField = this.data.find(
      (field: FieldConfig) => field.field_name === 'project_id'
    );
    if (projectField && this.masterData['projects']) {
      projectField.options = this.transformMasterDataToOptions(
        this.masterData['projects'],
        'project_id'
      );
    }
    // Also bind to primary-level project field (without _id suffix)
    const primaryProjectField = this.data.find(
      (field: FieldConfig) => field.field_name === 'project'
    );
    if (primaryProjectField && this.masterData['projects']) {
      primaryProjectField.options = this.transformMasterDataToOptions(
        this.masterData['projects'],
        'project'
      );
    }
    const productField = this.data.find(
      (field: FieldConfig) => field.field_name === 'product_id'
    );
    if (productField && this.masterData['products']) {
      productField.options = this.transformMasterDataToOptions(
        this.masterData['products'],
        'product_id'
      );
    }
    // Also bind to primary-level product field (without _id suffix)
    const primaryProductField = this.data.find(
      (field: FieldConfig) => field.field_name === 'product'
    );
    if (primaryProductField && this.masterData['products']) {
      primaryProductField.options = this.transformMasterDataToOptions(
        this.masterData['products'],
        'product'
      );
    }
    const productCategoryField = this.data.find(
      (field: FieldConfig) => field.field_name === 'product_category_id'
    );
    if (productCategoryField && this.masterData['product-categories']) {
      productCategoryField.options = this.transformMasterDataToOptions(
        this.masterData['product-categories'],
        'product_category_id'
      );
    }
    const natureOfGoodField = this.data.find(
      (field: FieldConfig) => field.field_name === 'nature_of_good_id'
    );
    if (natureOfGoodField && this.masterData['nature-of-goods']) {
      natureOfGoodField.options = this.transformMasterDataToOptions(
        this.masterData['nature-of-goods'],
        'nature_of_good_id'
      );
    }
    const locationField = this.data.find(
      (field: FieldConfig) => field.field_name === 'location_id'
    );
    if (locationField && this.masterData['locations']) {
      locationField.options = this.locationOptions;
    }
    const registrationField = this.data.find(
      (field: FieldConfig) => field.field_name === 'registration_type'
    );
    if (registrationField && this.masterData['registration-types']) {
      registrationField.options = this.transformMasterDataToOptions(
        this.masterData['registration-types'],
        'registration_type'
      );
    }

    // Bind units options to units field (in items table)
    const unitsField = this.data.find(
      (field: FieldConfig) => field.field_name === 'unit_id'
    );
    if (unitsField && this.unitsOptions.length > 0) {
      unitsField.options = this.normalizeUnitsOptions(this.unitsOptions);
    }

    // Bind place_of_supply options (states)
    const placeOfSupplyField = this.data.find(
      (field: FieldConfig) => field.field_name === 'place_of_supply_id' || field.field_name === 'place_of_supply'
    );
    if (placeOfSupplyField && Array.isArray(this.stateOptions) && this.stateOptions.length > 0) {
      placeOfSupplyField.options = this.normalizeOptions(this.stateOptions);
    }

    // Also assign using setFieldOptions to ensure it's available
    if (this.stateOptions.length > 0) {
      this.setFieldOptions('place_of_supply_id', this.normalizeOptions(this.stateOptions));
      this.setFieldOptions('place_of_supply', this.normalizeOptions(this.stateOptions));
    }

    // Bind file-types to file_type field (in files table)
    const fileTypeField = this.data.find(
      (field: FieldConfig) => field.field_name === 'file_type'
    );
    if (fileTypeField && this.masterData['file-types']) {
      fileTypeField.options = this.transformMasterDataToOptions(
        this.masterData['file-types'],
        'file_type'
      );
    }

    // Bind sales_type options
    const salesTypeField = this.data.find(
      (field: FieldConfig) => field.field_name === 'sales_type'
    );
    if (salesTypeField && this.salesTypeOptions.length > 0) {
      salesTypeField.options = this.salesTypeOptions;
    }
    // Also assign using setFieldOptions to ensure it's available
    if (this.salesTypeOptions.length > 0) {
      this.setFieldOptions('sales_type', this.salesTypeOptions);
    }
  }

  private transformMasterDataToOptions(masterData: any[], fieldType: string): any[] {
    if (!Array.isArray(masterData) || masterData.length === 0) {
      return [];
    }

    return masterData.map((item: any) => {
      // Handle different master data structures
      let value: any = null;
      let label: string = '';

      value = item.value ?? item.id ?? item.code ?? item.name ?? null;
      label = item.label ?? item.name ?? item.display_name ?? item.code ?? String(value ?? '');

      return {
        id: value,
        value: value,
        label: label,
        name: label,
        raw: item
      };
    });
  }

  private applyClientOptionsToField(): void {
    if (!this.clientOptions.length) {
      return;
    }

    this.setFieldOptions('client_id', this.clientOptions);
  }

  private applyTemplateOptionsToField(): void {
    const options = this.getTemplateOptionEntries();
    if (!options.length) {
      return;
    }

    const templateField = [...(this.primaryFields ?? []), ...(this.data ?? [])].find(
      (field: FieldConfig) => field.field_name === 'form_template_id'
    );
    if (templateField) {
      templateField.options = options;
      if (!templateField.default_value && this.currentTemplateId) {
        templateField.default_value = this.currentTemplateId;
      }
    }

    const control = this.customerForm?.get(this.getFieldKey('form_template_id'));
    if (control) {
      const normalizedValue = this.normalizeIdValue(control.value);
      if (!normalizedValue) {
        const candidate =
          this.currentTemplateId ??
          options.find(opt => opt.value !== undefined && opt.value !== null)?.value ??
          null;
        if (candidate !== null && candidate !== undefined) {
          control.patchValue(candidate, { emitEvent: false });
        }
      }
    }
  }

  private getTemplateOptionEntries(): any[] {
    return (this.templateOptions ?? []).map((tpl: any, index: number) => {
      const id = this.normalizeIdValue(
        tpl?.id ?? tpl?.template_id ?? tpl?.value ?? tpl?.templateId ?? tpl?.uuid
      );
      const label =
        tpl?.name ??
        tpl?.template_name ??
        tpl?.display_name ??
        tpl?.label ??
        `Template ${index + 1}`;
      return {
        id: id ?? label,
        value: id ?? label,
        label,
        name: label,
        raw: tpl
      };
    });
  }

  private setFormTemplateControlValue(templateId: string | null): void {
    if (!this.customerForm) {
      return;
    }
    const control = this.customerForm.get(this.getFieldKey('form_template_id'));
    if (!control) {
      return;
    }
    const normalized = templateId ? String(templateId) : null;
    if (normalized !== null) {
      control.patchValue(normalized, { emitEvent: false });
    } else if (!control.value) {
      control.patchValue(null, { emitEvent: false });
    }
  }

  private async fetchTemplateForCurrentSelection(): Promise<any> {
    if (this.currentTemplateId) {
      try {
        const response = await lastValueFrom(
          this.templateService.getSOFormTemplateById(this.currentTemplateId)
        );
        if (response) {
          return response;
        }
      } catch (error) {
        console.error('Error loading selected template. Falling back to default.', error);
      }
    }

    const fallbackResponse = await lastValueFrom(
      this.templateService.getClientSoDefaultTemplate()
    );
    const fallbackId = this.normalizeIdValue(
      fallbackResponse?.data?.template?.id ??
      fallbackResponse?.template?.id ??
      fallbackResponse?.data?.id
    );
    if (fallbackId) {
      this.currentTemplateId = fallbackId;
    }
    return fallbackResponse;
  }

  private async handleClientChange(clientId: any, initialLoad: boolean = false) {
    const clientCodeKey = this.getFieldKey('client_code');
    const shipToKey = this.getFieldKey('ship_to');
    const shippingAddressKey = this.getFieldKey('shipping_address');
    const placeOfSupplyKey = this.getFieldKey('place_of_supply_id');
    const clientIdKey = this.getFieldKey('client_id');
    const billToKey = this.getFieldKey('bill_to');
    const registrationNoKey = this.getFieldKey('registration_no');
    const registrationTypeKey = this.getFieldKey('registration_type');
    const customerGstKey = this.getFieldKey('customer_gst_number');
    const subsidiaryGstKey = this.getFieldKey('subsidiary_gst_number');

    if (!clientId) {
      if (initialLoad) {
        this.currentClientInfo = null;
        this.currentSubsidiaryInfo = null;
        this.calculatedTaxType = null;
        this.calculateAndUpdateTaxType();
        return;
      }
      const resetPayload: Record<string, any> = {};
      this.selectedShippingAddressId = null;
      this.selectedBillingAddressId = null;
      this.selectedSubsidiaryId = null;
      this.selectedLocationId = null;
      this.selectedPlaceOfSupplyId = null;

      if (this.customerForm.get(clientCodeKey)) {
        resetPayload[clientCodeKey] = null;
      }
      if (this.customerForm.get(shipToKey)) {
        resetPayload[shipToKey] = null;
      }
      if (this.customerForm.get(shippingAddressKey)) {
        resetPayload[shippingAddressKey] = null;
      }
      if (this.customerForm.get(clientIdKey)) {
        resetPayload[clientIdKey] = null;
      }
      if (this.customerForm.get(billToKey)) {
        resetPayload[billToKey] = null;
      }
      if (this.customerForm.get(registrationNoKey)) {
        resetPayload[registrationNoKey] = null;
      }
      if (this.customerForm.get(registrationTypeKey)) {
        resetPayload[registrationTypeKey] = null;
      }
      if (this.customerForm.get(customerGstKey)) {
        resetPayload[customerGstKey] = null;
      }
      if (this.customerForm.get(subsidiaryGstKey)) {
        resetPayload[subsidiaryGstKey] = null;
      }
      if (this.customerForm.get(placeOfSupplyKey)) {
        resetPayload[placeOfSupplyKey] = null;
      }

      if (Object.keys(resetPayload).length > 0) {
        this.customerForm.patchValue(resetPayload, { emitEvent: false });
      }

      this.setFieldOptions('ship_to', []);
      this.setFieldOptions('bill_to', []);
      this.setFieldOptions('subsidiary_id', []);
      this.setFieldOptions('location', []);
      this.setFieldOptions('customer_gst_number', []);
      this.setFieldOptions('subsidiary_gst_number', []);
      this.subsidiariesData = [];
      this.currentClientInfo = null;
      this.currentSubsidiaryInfo = null;
      this.calculatedTaxType = null;
      this.calculateAndUpdateTaxType();
      return;
    }

    try {
      const normalizedClientId =
        typeof clientId === 'object'
          ? clientId?.id ?? clientId?.value ?? clientId?.client_id ?? null
          : clientId;

      if (!normalizedClientId) {
        if (initialLoad) {
          this.currentClientInfo = null;
          this.currentSubsidiaryInfo = null;
          this.calculatedTaxType = null;
          this.calculateAndUpdateTaxType();
          return;
        }
        const resetPayload: Record<string, any> = {};
        this.selectedShippingAddressId = null;
        this.selectedBillingAddressId = null;
        this.selectedSubsidiaryId = null;
        this.selectedLocationId = null;
        this.selectedPlaceOfSupplyId = null;

        if (this.customerForm.get(clientCodeKey)) {
          resetPayload[clientCodeKey] = null;
        }
        if (this.customerForm.get(shipToKey)) {
          resetPayload[shipToKey] = null;
        }
        if (this.customerForm.get(shippingAddressKey)) {
          resetPayload[shippingAddressKey] = null;
        }
        if (this.customerForm.get(clientIdKey)) {
          resetPayload[clientIdKey] = null;
        }
        if (this.customerForm.get(billToKey)) {
          resetPayload[billToKey] = null;
        }
        if (this.customerForm.get(registrationNoKey)) {
          resetPayload[registrationNoKey] = null;
        }
        if (this.customerForm.get(registrationTypeKey)) {
          resetPayload[registrationTypeKey] = null;
        }
        if (this.customerForm.get(customerGstKey)) {
          resetPayload[customerGstKey] = null;
        }
        if (this.customerForm.get(subsidiaryGstKey)) {
          resetPayload[subsidiaryGstKey] = null;
        }
        if (this.customerForm.get(placeOfSupplyKey)) {
          resetPayload[placeOfSupplyKey] = null;
        }
        if (Object.keys(resetPayload).length > 0) {
          this.customerForm.patchValue(resetPayload, { emitEvent: false });
        }
        this.setFieldOptions('ship_to', []);
        this.setFieldOptions('bill_to', []);
        this.setFieldOptions('subsidiary_id', []);
        this.setFieldOptions('location', []);
        this.setFieldOptions('customer_gst_number', []);
        this.setFieldOptions('subsidiary_gst_number', []);
        this.subsidiariesData = [];
        this.currentClientInfo = null;
        this.currentSubsidiaryInfo = null;
        this.calculatedTaxType = null;
        this.calculateAndUpdateTaxType();
        return;
      }

      const clientRes: any = await lastValueFrom(
        this.clientService.getClientById(normalizedClientId, true)
      );

      // Handle response structure: when include_subsidiary=true, data has both client and subsidiaries
      let clientData = clientRes?.data?.client || clientRes?.data;

      if (!clientData) {
        this.currentClientInfo = null;
        this.calculateAndUpdateTaxType();
        return;
      }

      this.currentClientInfo = clientData;
      const subsidiaries = clientRes?.data?.subsidiaries || [];

      // Populate Client GST options from client tax info
      const clientTaxInfo = clientData.client_tax_info || [];
      const clientShippingAddress = clientData.client_shipping_address || {};
      const customerGstOptions = Array.isArray(clientTaxInfo)
        ? clientTaxInfo.map((gst: any) => ({
          label: gst.gst_number || '',
          value: gst.gst_number || ''
        }))
        : [];

      this.setFieldOptions('customer_gst_number', customerGstOptions);

      // Set default Client GST if available
      const defaultGst = clientTaxInfo.find((tax: any) => tax.mark_as_default);
      if (defaultGst && this.customerForm.get(customerGstKey) && !initialLoad) {
        this.customerForm.patchValue({ [customerGstKey]: defaultGst.gst_number }, { emitEvent: false });
      }

      // Auto-fill place_of_supply for SALES from the CUSTOMER's state.
      // Order: (1) GST first 2 digits if customer has a GSTIN, (2) default
      // client_tax_info.state_id (B2C), (3) client_shipping_address.shipping_state (legacy).
      if (!initialLoad) {
        const placeOfSupplyFormControl = this.customerForm?.get(this.getFieldKey('place_of_supply_id'));
        if (placeOfSupplyFormControl) {
          const defaultClientTaxInfo =
            (Array.isArray(clientTaxInfo) && (clientTaxInfo.find((t: any) => t?.mark_as_default) ?? clientTaxInfo[0])) || null;
          let valueToSet: string | null = null;

          // (1) GST-based: first 2 digits of customer GST → state_code
          const clientGst = defaultClientTaxInfo?.gst_number || '';
          const gstStateCode = this.extractStateCodeFromGst(clientGst);
          if (gstStateCode) {
            const matchByGst = (this.stateOptions || []).find((opt: any) =>
              String(opt.state_code) === gstStateCode ||
              String(opt.value).padStart(2, '0') === gstStateCode ||
              Number(opt.value) === Number(gstStateCode) ||
              Number(opt.id) === Number(gstStateCode)
            );
            valueToSet = matchByGst ? String(matchByGst.value || matchByGst.id) : gstStateCode;
          }

          // (2) B2C: derive from client_tax_info.state_id (snowflake or legacy state_code)
          if (!valueToSet) {
            const clientStateId = defaultClientTaxInfo?.state_id;
            if (clientStateId) {
              const matchByStateId = (this.stateOptions || []).find((opt: any) =>
                String(opt.state_id) === String(clientStateId) ||
                String(opt.id) === String(clientStateId) ||
                String(opt.value) === String(clientStateId) ||
                String(opt.state_code) === String(clientStateId) ||
                Number(opt.state_code) === Number(clientStateId)
              );
              if (matchByStateId) {
                valueToSet = String(matchByStateId.value || matchByStateId.id);
              }
            }
          }

          // (3) Legacy: client_shipping_address.shipping_state
          if (!valueToSet && clientShippingAddress?.shipping_state) {
            valueToSet = String(clientShippingAddress.shipping_state);
          }

          if (valueToSet) {
            placeOfSupplyFormControl.setValue(valueToSet, { emitEvent: false });
          }
        }
      }

      const clientControl = this.customerForm.get(clientIdKey);
      if (clientControl) {
        // Format the client data into an option object
        const clientOption = ClientTypeaheadUtils.formatClientOption(clientData);

        // Update the control value (without emitting event to avoid loop)
        clientControl.setValue(clientOption, { emitEvent: false });
        if (this.mode === 'view') {
          clientControl.setValue(clientOption?.client_name, { emitEvent: false });
        }
        // Ensure this client is in the options list
        const existingIndex = this.clientOptions.findIndex(c => String(c.value) === String(normalizedClientId));
        if (existingIndex >= 0) {
          this.clientOptions[existingIndex] = clientOption;
        } else {
          this.clientOptions.push(clientOption);
        }
      }

      const clientCode = clientData?.client_code ?? clientData?.code ?? '';

      const patchPayload: Record<string, any> = {};

      if (this.customerForm.get(clientCodeKey)) {
        patchPayload[clientCodeKey] = clientCode || null;
      }

      if (this.customerForm.get(registrationNoKey) && !initialLoad) {
        const registrationNumber =
          clientData?.client_basic_details?.registration_number ??
          clientData?.client_basic_details?.registration_no ??
          null;
        patchPayload[registrationNoKey] = registrationNumber ?? null;
      }

      if (this.customerForm.get(registrationTypeKey) && !initialLoad) {
        const registrationType = clientData?.client_basic_details?.registration_type ?? null;
        patchPayload[registrationTypeKey] = registrationType ?? null;
      }

      // Auto-fill sales_type from client's client_category
      const salesTypeKey = this.getFieldKey('sales_type');
      if (this.customerForm.get(salesTypeKey) && !initialLoad) {
        // Get client_category from client data (this maps to sales_type options)
        const clientCategory = clientData?.client_category ?? null;
        
        if (clientCategory && this.salesTypeOptions.length > 0) {
          // Find matching sales type by ID
          const matchingSalesType = this.salesTypeOptions.find((opt: any) => 
            String(opt.id) === String(clientCategory) ||
            String(opt.value) === String(clientCategory)
          );
          
          if (matchingSalesType) {
            patchPayload[salesTypeKey] = matchingSalesType.id || matchingSalesType.value;
          }
        }
      }

      // Auto-fill phone_number custom field from client's basic details
      const phoneNumberKey = this.getFieldKey('phone_number');
      if (this.customerForm.get(phoneNumberKey) && !initialLoad) {
        const clientPhone = clientData?.client_basic_details?.client_phone ?? 
                           clientData?.client_basic_details?.phone ?? null;
        if (clientPhone) {
          patchPayload[phoneNumberKey] = clientPhone;
        }
      }

      // Auto-fill alternate_phone_number custom field from client's basic details
      const altPhoneNumberKey = this.getFieldKey('alternate_phone_number');
      if (this.customerForm.get(altPhoneNumberKey) && !initialLoad) {
        const altPhone = clientData?.client_basic_details?.client_alt_phone ?? 
                        clientData?.client_basic_details?.alt_phone ?? null;
        if (altPhone) {
          patchPayload[altPhoneNumberKey] = altPhone;
        }
      }

      // Get tax info list for GST and address handling
      const taxInfoList = Array.isArray(clientData?.client_tax_info)
        ? clientData.client_tax_info
        : [];

      if (this.customerForm.get(customerGstKey) && !initialLoad) {
        const defaultTaxInfo =
          taxInfoList.find((info: any) => info?.mark_as_default) ?? taxInfoList[0] ?? null;
        const gstNumber = defaultTaxInfo?.gst_number ?? defaultTaxInfo?.customer_gst_number ?? null;
        patchPayload[customerGstKey] = gstNumber ?? null;
      }

      const billToOptions = taxInfoList
        .filter((info: any) => info?.id && info?.address)
        .map((info: any) => ({
          id: String(info.id),
          value: String(info.id),
          label: info.address // TODO: change this from address to name on column addition at backend
        }));
      this.setFieldOptions('bill_to', billToOptions);

      // Set billing address value - only store the ID
      let billToValue: string | null = null;
      if (initialLoad && this.selectedBillingAddressId) {
        // On initial load, use the stored billing address ID from backend
        billToValue = String(this.selectedBillingAddressId);
      } else if (billToOptions.length > 0) {
        // Otherwise, use the default or first option ID
        const defaultTaxInfo = taxInfoList.find((info: any) => info?.mark_as_default) ?? taxInfoList[0];
        if (defaultTaxInfo?.id) {
          billToValue = String(defaultTaxInfo.id);
          this.selectedBillingAddressId = billToValue;
        }
      }

      if (this.customerForm.get(billToKey) && billToValue) {
        patchPayload[billToKey] = billToValue;
      }

      const shipping = clientData?.client_shipping_address ?? {};
      const shipToOptions = shipping?.id && shipping?.shipping_address ? [{
        id: String(shipping.id),
        value: String(shipping.id),
        label: shipping.shipping_address // TODO: change this from address to name on column addition at backend
      }] : [];
      let shipToValue: string | null = null;
      if (initialLoad && this.selectedShippingAddressId) {
        shipToValue = String(this.selectedShippingAddressId);
      } else if (shipToOptions.length > 0 && shipping?.id) {
        shipToValue = String(shipping.id);
        this.selectedShippingAddressId = shipToValue;
      }
      this.setFieldOptions('ship_to', shipToOptions);
      if (this.customerForm.get(shipToKey) && shipToValue) {
        patchPayload[shipToKey] = shipToValue;
      }
      if (this.customerForm.get(shippingAddressKey) && shipping?.shipping_address) {
        patchPayload[shippingAddressKey] = shipping.shipping_address;
      }

      // Fetch and set subsidiary options (pass subsidiaries from API response)
      await this.loadAndSetSubsidiaryOptions(subsidiaries, initialLoad, patchPayload);
      if (Object.keys(patchPayload).length > 0) {
        this.customerForm.patchValue(patchPayload, { emitEvent: false });
      }
      this.calculateAndUpdateTaxType();
    } catch (err: any) {
      this.toasterService.showMessage(this.toasterService.MESSAGE_TYPES.ERROR, err);
      this.currentClientInfo = null;
      this.currentSubsidiaryInfo = null;
      this.calculatedTaxType = null;
      this.calculateAndUpdateTaxType();
    }
  }

  /**
   * Load subsidiaries for the client and set field options
   */
  private async loadAndSetSubsidiaryOptions(
    subsidiaries: any[],
    initialLoad: boolean,
    patchPayload: Record<string, any>
  ): Promise<void> {
    const subsidiaryIdKey = this.getFieldKey('subsidiary_id');

    // Use subsidiaries passed from API response
    const subsidiariesArray = Array.isArray(subsidiaries) ? subsidiaries : [];

    if (subsidiariesArray.length === 0) {
      // No subsidiaries found, clear options and show message
      this.setFieldOptions('subsidiary_id', []);
      this.setFieldOptions('location', []);
      this.selectedLocationId = null;
      const locationKey = this.getFieldKey('location');
      const subsidiaryGstKey = this.getFieldKey('subsidiary_gst_number');
      const placeOfSupplyKey = this.getFieldKey('place_of_supply_id');
      if (this.customerForm.get(locationKey)) {
        this.customerForm.patchValue({ [locationKey]: null }, { emitEvent: false });
      }
      if (this.customerForm.get(subsidiaryGstKey)) {
        this.customerForm.patchValue({ [subsidiaryGstKey]: null }, { emitEvent: false });
      }
      if (this.customerForm.get(placeOfSupplyKey)) {
        this.customerForm.patchValue({ [placeOfSupplyKey]: null }, { emitEvent: false });
      }
      this.subsidiariesData = [];
      this.currentSubsidiaryInfo = null;
      this.toasterService.showMessage(
        this.toasterService.MESSAGE_TYPES.WARNING,
        'No subsidiary is linked to this client.'
      );
      this.calculateAndUpdateTaxType();
      return;
    }

    // Store full subsidiary data for location handling
    this.subsidiariesData = subsidiariesArray;
    this.currentSubsidiaryInfo = null;

    // Create options with only ID and name/label
    const subsidiaryOptions = this.subsidiariesData.map((subsidiary: any) => ({
      id: String(subsidiary.id || subsidiary.subsidiary_id || ''),
      value: String(subsidiary.id || subsidiary.subsidiary_id || ''),
      label: subsidiary.name ||
        subsidiary.subsidiary_name ||
        subsidiary.display_name ||
        String(subsidiary.id || '')
    }));

    // Set the options for subsidiary_id field
    this.setFieldOptions('subsidiary_id', subsidiaryOptions);

    // Handle subsidiary selection
    if (initialLoad && this.selectedSubsidiaryId && subsidiaryOptions.length > 0) {
      // On initial load, set the subsidiary value and trigger location handling
      patchPayload[subsidiaryIdKey] = String(this.selectedSubsidiaryId);
      // Handle location and place_of_supply for the selected subsidiary on initial load
      await this.handleSubsidiaryChange(String(this.selectedSubsidiaryId), true);
    }
  }

  /**
   * Handle subsidiary change and update location field
   */
  private async handleSubsidiaryChange(subsidiaryId: any, initialLoad: boolean = false): Promise<void> {
    const normalizedSubsidiaryId = this.normalizeIdValue(subsidiaryId);

    if (!normalizedSubsidiaryId) {
      // Clear location and place_of_supply options if no subsidiary selected
      this.setFieldOptions('location', []);
      this.setFieldOptions('subsidiary_gst_number', []);
      this.selectedLocationId = null;
      const locationKey = this.getFieldKey('location');
      const subsidiaryGstKey = this.getFieldKey('subsidiary_gst_number');
      if (this.customerForm.get(locationKey)) {
        this.customerForm.patchValue({ [locationKey]: null }, { emitEvent: false });
      }
      if (this.customerForm.get(subsidiaryGstKey)) {
        this.customerForm.patchValue({ [subsidiaryGstKey]: null }, { emitEvent: false });
      }
      this.currentSubsidiaryInfo = null;
      this.calculateAndUpdateTaxType();
      return;
    }

    // Find the selected subsidiary from stored data
    const selectedSubsidiary = this.subsidiariesData.find(
      (sub: any) => String(sub.id || sub.subsidiary_id) === normalizedSubsidiaryId
    );

    if (!selectedSubsidiary) {
      console.warn('Selected subsidiary not found in stored data');
      this.setFieldOptions('location', []);
      this.setFieldOptions('subsidiary_gst_number', []);
      this.selectedLocationId = null;
      this.currentSubsidiaryInfo = null;
      this.calculateAndUpdateTaxType();
      return;
    }
    this.currentSubsidiaryInfo = selectedSubsidiary;

    // Populate subsidiary GST options from tax_information
    const taxInfo = selectedSubsidiary.tax_information || selectedSubsidiary.tax_info || [];
    const subsidiaryGstOptions = Array.isArray(taxInfo)
      ? taxInfo.map((tax: any) => ({
        label: tax.gst_number || '',
        value: tax.gst_number || ''
      }))
      : [];

    this.setFieldOptions('subsidiary_gst_number', subsidiaryGstOptions);

    const defaultGst = subsidiaryGstOptions.find((gst: any) => gst?.mark_as_default) || subsidiaryGstOptions[0] || null;
    if (defaultGst && !initialLoad) {
      const subsidiaryGstKey = this.getFieldKey('subsidiary_gst_number');
      if (this.customerForm.get(subsidiaryGstKey)) {
        this.customerForm.patchValue({ [subsidiaryGstKey]: defaultGst.value }, { emitEvent: false });
      }
    }

    const locationTaxInfo = (Array.isArray(taxInfo) && taxInfo.length > 0
      ? taxInfo
      : []).filter((tax: any) => !tax?.is_deleted && tax?.id);
    const locationOptions = locationTaxInfo
      .map((tax: any) => ({
        id: String(tax.id),
        value: String(tax.id),
        label: tax.address || tax.gst_number || String(tax.id),
        name: tax.address || tax.gst_number || String(tax.id),
        gst_number: tax.gst_number,
        address: tax.address,
        state_id: tax.state_id,
        state_code: tax.state_code,
        mark_as_default: tax.mark_as_default,
        raw: tax
      }));

    // Set the location field options
    this.locationOptions = locationOptions;
    this.setFieldOptions('location', locationOptions);
    this.setFieldOptions('location_id', locationOptions);

    const defaultLocation = locationTaxInfo.find((tax: any) => tax?.mark_as_default) || locationTaxInfo[0] || null;

    // const placeOfSupplyOptions = addresses
    //   .filter((addr: any) => !addr?.is_deleted && addr?.id)
    //   .map((addr: any) => {
    //     // Create a formatted label with address and address_type if available
    //     const addressLabel = addr.address || 
    //                        (addr.address_type ? `${addr.address_type}` : 'Address');
    //     const subsidiaryName = selectedSubsidiary.name || selectedSubsidiary.subsidiary_name || '';
    //     const label = subsidiaryName 
    //       ? `${subsidiaryName} - ${addressLabel}`
    //       : addressLabel;

    //     return {
    //       id: String(addr.id),
    //       value: String(addr.id),
    //       label: label,
    //       address: addr.address,
    //       address_type: addr.address_type,
    //       subsidiary_id: String(selectedSubsidiary.id || selectedSubsidiary.subsidiary_id || ''),
    //       ...addr
    //     };
    //   });

    // Set the place_of_supply field options (only addresses from selected subsidiary)

    // Handle location value setting
    const locationKey = this.getFieldKey('location');
    if (this.customerForm.get(locationKey)) {
      let locationValue: string | null = null;

      if (initialLoad && this.selectedLocationId) {
        // On initial load, use the stored location ID from backend
        locationValue = String(this.selectedLocationId);
        if (locationValue) {
          this.customerForm.patchValue({ [locationKey]: locationValue }, { emitEvent: false });
          // Call handleLocationChange to store the ID but not update GST (initialLoad = true)
          this.handleLocationChange(locationValue, true);
        }
      } else if (initialLoad && !this.selectedLocationId && defaultLocation && defaultLocation.id) {
        // On initial load, if backend didn't provide a selectedLocationId but subsidiary has a default location, prefill it
        locationValue = String(defaultLocation.id);
        this.customerForm.patchValue({ [locationKey]: locationValue }, { emitEvent: false });
        // Use initialLoad=true to avoid overwriting any backend GST value
        this.handleLocationChange(locationValue, true);
      } else if (!initialLoad) {
        //If there's exactly one location option for this subsidiary, auto-select it
        if (Array.isArray(locationOptions) && locationOptions.length === 1) {
          const onlyLocation = locationOptions[0];
          locationValue = onlyLocation?.id ? String(onlyLocation.id) : onlyLocation?.value ? String(onlyLocation.value) : null;
          if (locationValue) {
            this.customerForm.patchValue({ [locationKey]: locationValue }, { emitEvent: false });
            // Trigger location change handler to update GST and tax type
            this.handleLocationChange(locationValue, false);
          }
        } else if (defaultLocation && defaultLocation.id) {
          // If multiple locations exist but one is marked default, preselect that one
          locationValue = String(defaultLocation.id);
          this.customerForm.patchValue({ [locationKey]: locationValue }, { emitEvent: false });
          this.handleLocationChange(locationValue, false);
        } else {
          // Clear location and subsidiary GST when subsidiary changes (not on initial load)
          this.customerForm.patchValue({ [locationKey]: null }, { emitEvent: false });
          this.selectedLocationId = null;

          const subsidiaryGstKey = this.getFieldKey('subsidiary_gst_number');
          if (this.customerForm.get(subsidiaryGstKey)) {
            this.customerForm.patchValue({ [subsidiaryGstKey]: null }, { emitEvent: false });
          }
        }
      }
    }

    // Handle place_of_supply value when subsidiary changes


    // Store the selected subsidiary ID
    this.selectedSubsidiaryId = normalizedSubsidiaryId;
    this.calculateAndUpdateTaxType();
    
    // When subsidiary is selected, fetch fiscal period if posting_date is already set
    const postingDateKey = this.getFieldKey('posting_date');
    const postingDateControl = this.customerForm?.get(postingDateKey);
    if (postingDateControl?.value) {
      FiscalPeriodUtils.fetchFiscalPeriodByDate(
        this.mastersService,
        this.toasterService,
        normalizedSubsidiaryId,
        postingDateControl.value,
        (fiscalPeriod) => {
          const fiscalPeriodKey = this.getFieldKey('fiscal_period_id');
          const fiscalPeriodControl = this.customerForm.get(fiscalPeriodKey);
          
          if (fiscalPeriodControl) {
            const fiscalPeriodIdStr = String(fiscalPeriod.id);
            const fiscalPeriodOption = {
              id: fiscalPeriodIdStr,
              value: fiscalPeriodIdStr,
              name: fiscalPeriod.period_name,
              label: fiscalPeriod.period_name
            };
            
            const fiscalPeriodField = this.primaryFields?.find((f: any) => f.field_name === 'fiscal_period_id');
            if (fiscalPeriodField) {
              fiscalPeriodField.options = [fiscalPeriodOption];
            }
            
            fiscalPeriodControl.setValue(fiscalPeriodIdStr, { emitEvent: false });
            fiscalPeriodControl.disable({ emitEvent: false });
          }
          
          const postingPeriodKey = this.getFieldKey('posting_period');
          if (this.customerForm.get(postingPeriodKey)) {
            this.customerForm.patchValue({ [postingPeriodKey]: fiscalPeriod.period_name });
          }
        },
        () => {}
      );
    }
  }

  /**
   * Handle location change and update subsidiary GST number
   */
  private handleLocationChange(locationId: any, initialLoad: boolean = false): void {
    const normalizedLocationId = this.normalizeIdValue(locationId);

    // Store the selected location ID
    this.selectedLocationId = normalizedLocationId;

    // Don't update GST on initial load
    if (initialLoad) {
      return;
    }

    if (!normalizedLocationId) {
      // Clear subsidiary_gst_number if no location selected
      const subsidiaryGstKey = this.getFieldKey('subsidiary_gst_number');
      if (this.customerForm.get(subsidiaryGstKey)) {
        this.customerForm.patchValue({ [subsidiaryGstKey]: null }, { emitEvent: false });
      }
      this.calculateAndUpdateTaxType();
      return;
    }

    // Find the location in subsidiariesData
    let foundLocation: any = null;

    for (const subsidiary of this.subsidiariesData) {
      const taxInfoList = Array.isArray(subsidiary?.tax_information)
        ? subsidiary.tax_information
        : [];

      foundLocation = taxInfoList.find(
        (taxInfo: any) => String(taxInfo.id) === normalizedLocationId
      );

      if (foundLocation) {
        break;
      }
    }

    if (!foundLocation) {
      console.warn('Selected location not found in subsidiaries data');
      return;
    }

    // Update subsidiary_gst_number with the gst_number from the selected location
    const gstNumber = foundLocation.gst_number || '';
    const subsidiaryGstKey = this.getFieldKey('subsidiary_gst_number');

    if (this.customerForm.get(subsidiaryGstKey)) {
      this.customerForm.patchValue({ [subsidiaryGstKey]: gstNumber }, { emitEvent: false });
    }

    this.calculateAndUpdateTaxType();
  }

  /**
   * Handle subsidiary_gst_number change.
   * Place of Supply for sales is derived from the CUSTOMER's state (set in
   * handleClientChange / customer-GST change), not the subsidiary's GST.
   * This stays a no-op intentionally; setting POS from subsidiary GST would
   * incorrectly stamp the seller's state on every sale.
   */
  private handleSubsidiaryGstChange(gstNumber: any): void {
    if (!gstNumber) {
      return;
    }
    const gstString = typeof gstNumber === 'object'
      ? (gstNumber.value || gstNumber.gst_number || gstNumber.label)
      : String(gstNumber);
    if (!gstString || gstString.length < 2) {
      return;
    }
  }

  private extractStateCodeFromGst(gstNumber: any): string | null {
    if (gstNumber === null || gstNumber === undefined) {
      return null;
    }
    const gstString = String(gstNumber).trim();
    if (gstString.length < 2) {
      return null;
    }
    const code = gstString.substring(0, 2);
    return /^\d{2}$/.test(code) ? code : null;
  }

  private getStateIdFromGstNumber(gstNumber: any, gstType: 'customer' | 'subsidiary'): string | null {
    if (!gstNumber) {
      return null;
    }
    const gstString = String(gstNumber).trim();
    
    // Always extract state code directly from GST number (first 2 digits)
    // This ensures consistent comparison between customer and subsidiary GST numbers
    return this.extractStateCodeFromGst(gstString);
  }

  /**
  * Get state code from place_of_supply field
  */
  private getStateCodeFromPlaceOfSupply(): string | null {
    const placeOfSupplyKey = this.getFieldKey('place_of_supply_id');
    let placeOfSupplyValue = this.customerForm.get(placeOfSupplyKey)?.value;

    // Fallback to place_of_supply if place_of_supply_id is not found
    if (!placeOfSupplyValue) {
      const altKey = this.getFieldKey('place_of_supply');
      placeOfSupplyValue = this.customerForm.get(altKey)?.value;
    }
    
    if (!placeOfSupplyValue) {
      return null;
    }

    // Find the state option matching the place_of_supply value
    const stateOptions = this.stateOptions || [];
    const matchingState = stateOptions.find(
      (state: any) =>
        String(state.id) === String(placeOfSupplyValue) ||
        String(state.value) === String(placeOfSupplyValue) ||
        String(state.state_id) === String(placeOfSupplyValue) ||
        String(state.state_code) === String(placeOfSupplyValue)
    );

    if (matchingState && matchingState.state_code) {
      return String(matchingState.state_code).padStart(2, '0');
    }

    // If place_of_supply value itself is a state code (2 digits)
    if (/^\d{2}$/.test(String(placeOfSupplyValue))) {
      return String(placeOfSupplyValue);
    }

    return null;
  }

  private calculateTaxTypeFromGstNumbers(): string | null {
    if (!this.customerForm) {
      return null;
    }

    const subsidiaryGstKey = this.getFieldKey('subsidiary_gst_number');
    const subsidiaryGst = this.customerForm.get(subsidiaryGstKey)?.value;

    // Extract state code from place of supply
    const placeOfSupplyState = this.getStateCodeFromPlaceOfSupply();

    // If subsidiary GST exists but place of supply is missing, default to CGST+SGST
    if (subsidiaryGst && !placeOfSupplyState) {
      return 'CGST+SGST';
    }

    // If neither exists, return null (no calculation possible)
    if (!placeOfSupplyState || !subsidiaryGst) {
      return null;
    }

    // Extract state codes for comparison
    const subsidiaryState = this.getStateIdFromGstNumber(subsidiaryGst, 'subsidiary');

    // If subsidiary state is available but place of supply state is not, default to CGST+SGST
    if (subsidiaryState && !placeOfSupplyState) {
      return 'CGST+SGST';
    }

    if (!placeOfSupplyState || !subsidiaryState) {
      return null;
    }

    return placeOfSupplyState === subsidiaryState ? 'CGST+SGST' : 'IGST';
  }

  private updateTaxTypeInAllTables(taxType: string | null): void {
    if (!taxType || !this.customerForm) {
      return;
    }
    const taxTypeKey = this.getFieldKey('tax_type');
    const tableKeys = Array.from(this.tableFormArrays.keys());

    tableKeys.forEach((tableKey) => {
      const formArray = this.getTableFormArray(tableKey);
      if (formArray && formArray instanceof FormArray) {
        formArray.controls.forEach((rowControl) => {
          if (rowControl instanceof FormGroup) {
            const control = rowControl.get(taxTypeKey);
            if (control) {
              control.setValue(taxType, { emitEvent: false });
            }
          }
        });
      }
    });
  }

  private calculateAndUpdateTaxType(): void {
    // Always calculate and save silently so new items have a default tax type
    const taxType = this.calculateTaxTypeFromGstNumbers();
    this.calculatedTaxType = taxType;

    // If safe to update, then update the table
    if (taxType) {
      this.updateTaxTypeInAllTables(taxType);
    }
  }

  // Handle field change events emitted by app-dynamic-form
  async onChildFieldChanged(event: any) {
    if (!event || !event.key) return;
    const rawKey = String(event.key || '');
    const key = rawKey.toLowerCase();
    const value = event?.value ?? event;

    // Handle audit log filters (date/search/audit fields) in view mode
    if (
      this.mode === 'view' &&
      (key.includes('date') || key.includes('filter') || key.includes('search') || key.includes('audit'))
    ) {
      await this.handleAuditLogFiltering(rawKey, value, event);
    }

    switch (key) {
      case 'client_id':
        if (value && typeof value === 'object' && value.label) {
          const clientControl = this.customerForm?.get(this.getFieldKey('client_id'));
          if (clientControl) {
            clientControl.setValue(value, { emitEvent: false });
          }
        }
        await this.handleClientChange(value);
        break;
      case 'form_template_id':
      await this.onTemplateChange(value);
      break;
      case 'posting_date':
        if (value) {
          const subsidiaryId = this.customerForm.get(this.getFieldKey('subsidiary_id'))?.value;
          if (!subsidiaryId) {
              if (this.toasterService.warn) {
                  this.toasterService.warn('Please select a subsidiary first');
              } else {
                  // @ts-ignore
                  this.toasterService.error('Please select a subsidiary first');
              }
              const postingDateControl = this.customerForm.get(this.getFieldKey('posting_date'));
              if (postingDateControl) {
                  postingDateControl.setValue(null, { emitEvent: false });
              }
              return;
          }

          FiscalPeriodUtils.fetchFiscalPeriodByDate(
              this.mastersService,
              this.toasterService,
              subsidiaryId,
              value,
              (fiscalPeriod) => {
                const fiscalPeriodKey = this.getFieldKey('fiscal_period_id');
                const fiscalPeriodControl = this.customerForm.get(fiscalPeriodKey);
                
                if (fiscalPeriodControl) {
                  // Ensure ID is treated as string to prevent JS number precision loss
                  const fiscalPeriodIdStr = String(fiscalPeriod.id);
                  
                  // Create option for the fetched fiscal period
                  const fiscalPeriodOption = {
                    id: fiscalPeriodIdStr,
                    value: fiscalPeriodIdStr,
                    name: fiscalPeriod.period_name,
                    label: fiscalPeriod.period_name
                  };
                  
                  // Set options for the dropdown directly on the field data
                  const fiscalPeriodField = this.primaryFields?.find((f: any) => f.field_name === 'fiscal_period_id');
                  if (fiscalPeriodField) {
                    fiscalPeriodField.options = [fiscalPeriodOption];
                  }
                  
                  // Set the value as string
                  fiscalPeriodControl.setValue(fiscalPeriodIdStr, { emitEvent: false });
                  
                  // Disable the dropdown to make it read-only
                  fiscalPeriodControl.disable({ emitEvent: false });
                }
                
                const postingPeriodKey = this.getFieldKey('posting_period');
                if (this.customerForm.get(postingPeriodKey)) {
                    this.customerForm.patchValue({ [postingPeriodKey]: fiscalPeriod.period_name });
                }
              },
              () => {
                const fiscalPeriodKey = this.getFieldKey('fiscal_period_id');
                const fiscalPeriodControl = this.customerForm.get(fiscalPeriodKey);
                
                if (fiscalPeriodControl) {
                  // Clear options directly on the field data
                  const fiscalPeriodField = this.primaryFields?.find((f: any) => f.field_name === 'fiscal_period_id');
                  if (fiscalPeriodField) {
                    fiscalPeriodField.options = [];
                  }
                  fiscalPeriodControl.setValue(null, { emitEvent: false });
                  fiscalPeriodControl.disable({ emitEvent: false });
                }
                
                const postingPeriodKey = this.getFieldKey('posting_period');
                if (this.customerForm.get(postingPeriodKey)) {
                    this.customerForm.patchValue({ [postingPeriodKey]: null });
                }
              }
          );
        }
      break;
      case 'subsidiary_id':
        await this.handleSubsidiaryChange(value, false);
        break;
      case 'ship_to':
        this.handleShipToSelection(value);
        break;
      case 'bill_to':
        this.handleBillToSelection(value);
        break;
      case 'location':
        // Handle location change and update subsidiary GST number
        this.handleLocationChange(value, false);
        break;
      case 'tax_details_override':
        const shouldEnable = this.isTruthyValue(value);
        this.setTaxOverrideDependentFieldsState(shouldEnable);
        break;
      case 'customer_gst_number':
        this.calculateAndUpdateTaxType();
        break;
      case 'subsidiary_gst_number':
        // Update place_of_supply based on the GST number's state code (first 2 digits)
        this.handleSubsidiaryGstChange(value);
        this.calculateAndUpdateTaxType();
        break;
      case 'place_of_supply_id':
      case 'place_of_supply':
        this.calculateAndUpdateTaxType();
        break;
      default:
        // no-op for other fields
        break;
    }
  }

  /**
   * Handle autocomplete search for primary form fields
   */
  onPrimaryFormSearchQuery(event: { fieldName: string; query: string; field?: FieldConfig }): void {
    if (!event?.fieldName) {
      return;
    }

    if (event.fieldName === 'client_id') {
      // Handle async search for client
      this.handleClientSearch(event.query ?? '').catch(error => {
        console.error('Error in client search:', error);
      });
    }
  }


  private getFormValue(source: any, fieldName: string, fallback?: any): any {
    if (!this.customerForm) {
      return fallback;
    }

    const key = this.getFieldKey(fieldName);
    if (source && Object.prototype.hasOwnProperty.call(source, key)) {
      const value = source[key];
      if (value !== undefined) {
        return value;
      }
    }

    if (source && Object.prototype.hasOwnProperty.call(source, fieldName)) {
      const direct = source[fieldName];
      if (direct !== undefined) {
        return direct;
      }
    }

    const control = this.customerForm.get(key);
    if (control) {
      const controlValue =
        typeof (control as any).getRawValue === 'function'
          ? (control as any).getRawValue()
          : control.value;
      if (controlValue !== undefined) {
        return controlValue;
      }
    }

    return fallback;
  }

  private getRowValue(row: any, fieldName: string, fallback?: any): any {
    if (!row) return fallback;
    const key = this.getFieldKey(fieldName);
    if (Object.prototype.hasOwnProperty.call(row, key) && row[key] !== undefined) {
      return row[key];
    }
    if (Object.prototype.hasOwnProperty.call(row, fieldName) && row[fieldName] !== undefined) {
      return row[fieldName];
    }
    return fallback;
  }

  private normalizeIdValue(value: any): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'object') {
      const candidate =
        value.id ??
        value.value ??
        value.client_id ??
        value._id ??
        value.uuid ??
        value.code ??
        value.name ??
        null;
      return candidate !== null && candidate !== undefined ? String(candidate) : null;
    }

    return String(value);
  }

  private toNumberValue(value: any, fallback: number | null = null): number | null {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    if (typeof value === 'number') {
      return isNaN(value) ? fallback : value;
    }
    const numeric = Number(value);
    return isNaN(numeric) ? fallback : numeric;
  }

  private toIsoString(value: any): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value.toISOString();
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }
      const parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime()) && !trimmed.includes('T')) {
        return parsed.toISOString();
      }
      return trimmed;
    }
    return null;
  }

  private toStringValue(value: any): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length ? trimmed : null;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (typeof value === 'object') {
      if (value.label) return String(value.label);
      if (value.name) return String(value.name);
      if (value.value) return String(value.value);
      if (value.id) return String(value.id);
    }

    return null;
  }

  private getUnitName(unitId: string | null): string | null {
    if (!unitId || !this.unitsOptions || this.unitsOptions.length === 0) {
      return null;
    }

    const matchedUnit = this.unitsOptions.find((opt: any) => {
      const optId = String(opt.id ?? opt.value ?? '');
      const optCode = String(opt.code ?? '');
      const searchId = String(unitId);
      return optId === searchId || optCode === searchId;
    });

    if (matchedUnit) {
      return matchedUnit.name ?? matchedUnit.display_name ?? matchedUnit.label ?? null;
    }

    return null;
  }

  private pruneNullish<T extends Record<string, any>>(obj: T, removeEmptyString = false): T {
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (
        value === undefined ||
        value === null ||
        (removeEmptyString && typeof value === 'string' && value.trim() === '')
      ) {
        delete obj[key];
      }
    });
    return obj;
  }

  private getTableArray(key: string): FormArray | null {
    const normalizedKey = this.getFieldKey(key);
    const fromMap = this.tableFormArrays.get(normalizedKey);
    if (fromMap instanceof FormArray) {
      return fromMap;
    }

    const direct = this.customerForm?.get(normalizedKey);
    if (direct instanceof FormArray) {
      return direct;
    }

    return null;
  }

  private isRowMarkedDeleted(rowGroup: FormGroup): boolean {
    if (!rowGroup) return false;
    const value = rowGroup.get('is_deleted')?.value;
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  /**
   * Check if there is at least one non-deleted, non-empty item row in the items table
   */
  private hasAtLeastOneItemRow(): boolean {
    const itemsTableKey =
      this.SUB_TAB_KEYS?.ITEMS || this.getFieldKey('items') || 'items';
    const itemsTableArray = this.tableFormArrays.get(
      itemsTableKey
    ) as FormArray | undefined;

    if (!itemsTableArray || itemsTableArray.length === 0) {
      return false;
    }

    return itemsTableArray.controls.some((rowControl: AbstractControl) => {
      if (!(rowControl instanceof FormGroup)) {
        return false;
      }

      // Skip deleted rows
      if (this.isRowMarkedDeleted(rowControl)) {
        return false;
      }

      // Check if row has any meaningful data (e.g., item_id, quantity, pack, etc.)
      const hasAnyValue = Object.keys(rowControl.controls).some((key) => {
        const v = rowControl.get(key)?.value;
        return v !== null && v !== undefined && v !== '';
      });

      return hasAnyValue;
    });
  }

  /**
   * Find item table rows where quantity or pack is not greater than 0 (ignoring deleted/empty rows)
   * Mirrors the business validation used in vendor-po.ts for items.
   */
  private getInvalidItemQuantityOrPackRows(): number[] {
    const invalidRows: number[] = [];

    // Items table key is typically "items" in SUB_TAB_KEYS
    const itemsTableKey =
      this.SUB_TAB_KEYS?.ITEMS || this.getFieldKey('items') || 'items';
    const itemsTableArray = this.tableFormArrays.get(
      itemsTableKey
    ) as FormArray | undefined;

    if (!itemsTableArray || itemsTableArray.length === 0) {
      return invalidRows;
    }

    itemsTableArray.controls.forEach(
      (rowControl: AbstractControl, index: number) => {
        if (!(rowControl instanceof FormGroup)) {
          return;
        }

        // Skip deleted rows
        if (this.isRowMarkedDeleted(rowControl)) {
          return;
        }

        // Skip completely empty rows (no meaningful data)
        const hasAnyValue = Object.keys(rowControl.controls).some((key) => {
          const v = rowControl.get(key)?.value;
          return v !== null && v !== undefined && v !== '';
        });
        if (!hasAnyValue) {
          return;
        }

        const qtyControl = rowControl.get(this.getFieldKey('quantity'));
        const packControl = rowControl.get(this.getFieldKey('pack'));

        const qtyValue = qtyControl?.value;
        const packValue = packControl?.value;

        const qtyNumber =
          qtyValue === null || qtyValue === undefined || qtyValue === ''
            ? 0
            : Number(qtyValue);
        const packNumber =
          packValue === null || packValue === undefined || packValue === ''
            ? 0
            : Number(packValue);

        if (
          isNaN(qtyNumber) ||
          qtyNumber <= 0 ||
          isNaN(packNumber) ||
          packNumber <= 0
        ) {
          invalidRows.push(index + 1);
        }
      }
    );

    return invalidRows;
  }


  private syncTaxOverrideDependentFieldsFromForm(): void {
    if (!this.customerForm) {
      return;
    }

    const controlKey = this.getFieldKey('tax_details_override');
    const overrideControl = this.customerForm.get(controlKey);
    if (!overrideControl) {
      return;
    }

    const shouldEnable = this.isTruthyValue(overrideControl.value);
    this.setTaxOverrideDependentFieldsState(shouldEnable);
  }

  private isTruthyValue(value: any): boolean {
    if (value === true || value === 1) {
      return true;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y';
    }

    return false;
  }

  private setTaxOverrideDependentFieldsState(shouldEnable: boolean): void {
    if (!this.customerForm) {
      return;
    }

    const canEnable = shouldEnable && this.mode !== 'view';

    this.taxOverrideDependentFields.forEach(fieldName => {
      const controlKey = this.getFieldKey(fieldName);
      const control = this.customerForm?.get(controlKey);

      if (!control) {
        return;
      }

      if (canEnable) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    });
  }

  /**
   * Handle form field file upload
   */
  async onFormFileUpload(event: { file: File; field: any }): Promise<void> {
    await this.handleFileUpload({
      mode: 'form',
      file: event.file,
      field: event.field
    });
  }

  /**
   * Handle table field file upload
   */
  async onTableFileUpload(event: { file: File; field: any; rowIndex: number; rowId?: any }, tableKey: string): Promise<void> {
    await this.handleFileUpload({
      mode: 'table',
      file: event.file,
      field: event.field,
      tableKey,
      rowIndex: event.rowIndex
    });
  }

  /**
   * Handle file download request
   */
  async onFileDownloadRequested(event: { s3Key: string; fieldName: string; field: any }): Promise<void> {
    // The event parameter uses s3Key but we treat it as the file key value
    const fileKey = event.s3Key;

    if (!fileKey || typeof fileKey !== 'string' || fileKey.length === 0) {
      console.warn('No file key provided for file download');
      return;
    }

    const clientSOIdForDownload = this.clientSOId || '0';

    try {
      // Use openPreviewUrl to open file inline (preview) instead of forcing download
      await this.s3FileUploadService.openPreviewUrl(clientSOIdForDownload, fileKey, 'client');
    } catch (error: any) {
      this.toasterService.showMessage(
        this.toasterService.MESSAGE_TYPES.ERROR,
        error?.message || 'Failed to fetch preview URL'
      );
    }
  }

  /**
   * Handle file upload (form or table)
   */
  private async handleFileUpload(event: UploadEvent): Promise<void> {
    const { file, field } = event;
    if (!file) return;

    try {
      const clientSOIdForUpload = this.clientSOId || '0';
      const result = await this.s3FileUploadService.uploadFileToS3(
        file,
        clientSOIdForUpload,
        'client'
      );

      // Apply the upload result based on mode
      if (event.mode === 'form') {
        this.applyFormFieldUpload(result, field);
      } else {
        this.applyTableRowUpload(result, field, event.tableKey, event.rowIndex);
      }

      this.toasterService.showMessage(
        this.toasterService.MESSAGE_TYPES.SUCCESS,
        'File uploaded successfully'
      );
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to upload file';
      this.toasterService.showMessage(
        this.toasterService.MESSAGE_TYPES.ERROR,
        `Upload Error: ${errorMessage}`
      );
    } finally {
      this.cdr.detectChanges();
    }
  }

  /**
   * Update form control value
   */
  private updateControl(control: any, value: any): void {
    if (control) {
      control.setValue(value);
      control.markAsTouched();
      control.markAsDirty();
    }
  }

  /**
   * Apply file upload result to form field
   */
  private applyFormFieldUpload(result: S3UploadResult, field: any): void {
    const fileFieldKey = this.getFieldKey(field.field_name);
    // Store the s3Key value in the 'file' control
    this.updateControl(this.customerForm?.get(fileFieldKey), result.s3Key);

    // Store file name in the map (per field, not a global field)
    this.formFieldFileNamesMap.set(fileFieldKey, result.fileName);
  }

  /**
   * Get Files table configuration
   */
  private getFilesTableConfig(): { key: string; fields: FieldConfig[] } | null {
    if (!Array.isArray(this.dynamicFormTabs)) {
      return null;
    }

    for (const tab of this.dynamicFormTabs) {
      if (!Array.isArray(tab?.sub_tabs)) {
        continue;
      }
      for (const subTab of tab.sub_tabs) {
        if ((subTab?.type || '').toString().toLowerCase() !== 'table') {
          continue;
        }
        // Check if this is the Files table by sub_tab id (10) or title/name
        if (subTab?.id === 10 ||
          (subTab?.title && subTab.title.toLowerCase().includes('file')) ||
          (subTab?.name && subTab.name.toLowerCase().includes('file'))) {
          const key = subTab?.key || this.getFieldKey(subTab?.title || subTab?.name || '');
          const fields: FieldConfig[] = subTab?.content || [];
          return { key, fields };
        }
      }
    }

    // Fallback: try to find by checking if FormArray exists with common keys
    const possibleKeys = [
      this.getFieldKey('files'),
      this.getFieldKey('sub_tab_10'),
      'files',
      'sub_tab_10'
    ];

    for (const key of possibleKeys) {
      if (this.customerForm?.get(key) instanceof FormArray) {
        return { key, fields: [] };
      }
    }

    // Last resort: create FormArray with default key
    const defaultKey = this.getFieldKey('files');
    const formArray = this.getTableFormArray(defaultKey);
    if (formArray) {
      return { key: defaultKey, fields: [] };
    }

    return null;
  }

  /**
   * Apply file upload result to table row
   */
  private applyTableRowUpload(result: S3UploadResult, field: any, tableKey: string, rowIndex: number): void {
    const tableArray = this.getTableFormArray(tableKey);

    // Handle case where rowIndex is invalid (new file upload)
    const isValidIndex = rowIndex !== undefined && rowIndex !== null && rowIndex >= 0 && rowIndex < tableArray.length;
    let rowGroup: FormGroup | null = isValidIndex ? (tableArray.at(rowIndex) as FormGroup) : null;

    // If row doesn't exist or index is invalid, create a new one
    if (!rowGroup || !isValidIndex) {
      // Get the Files table config to create a proper row
      const filesConfig = this.getFilesTableConfig();

      if (filesConfig && filesConfig.key === tableKey) {
        // Create a new row with the proper fields
        rowGroup = this.createEmptyTableRow(filesConfig.fields);

        // Ensure id and is_deleted controls exist
        if (!rowGroup.get('id')) {
          rowGroup.addControl('id', this.fb.control(''));
        }
        if (!rowGroup.get('is_deleted')) {
          rowGroup.addControl('is_deleted', this.fb.control(false));
        }
      } else {
        // If we can't find the config, create a basic row
        rowGroup = this.fb.group({
          id: [''],
          is_deleted: [false]
        });
      }

      // Add the new row to the array
      tableArray.push(rowGroup);
      // Update rowIndex to the newly added row
      rowIndex = tableArray.length - 1;
      rowGroup = tableArray.at(rowIndex) as FormGroup;
    }

    if (rowGroup) {
      // Ensure all required controls exist before updating
      // The field.field_name should be 'file' (not 's3_key')
      const fileFieldKey = this.getFieldKey(field.field_name);
      const fileNameKey = this.getFieldKey('file_name');
      const fileTypeKey = this.getFieldKey('file_type');
      const fileSizeKey = this.getFieldKey('size_kb');

      // Add controls if they don't exist
      if (!rowGroup.get(fileFieldKey)) {
        rowGroup.addControl(fileFieldKey, this.fb.control(''));
      }
      if (!rowGroup.get(fileNameKey)) {
        rowGroup.addControl(fileNameKey, this.fb.control(''));
      }
      if (!rowGroup.get(fileTypeKey)) {
        rowGroup.addControl(fileTypeKey, this.fb.control(''));
      }
      if (!rowGroup.get(fileSizeKey)) {
        rowGroup.addControl(fileSizeKey, this.fb.control(0));
      }

      // Update the file field with the s3Key value (stored in 'file' control)
      this.updateControl(rowGroup.get(fileFieldKey), result.s3Key);

      // Update file_name field
      this.updateControl(rowGroup.get(fileNameKey), result.fileName);

      // Update file_type field
      // this.updateControl(rowGroup.get(fileTypeKey), result.contentType);

      // Update file size field
      this.updateControl(rowGroup.get(fileSizeKey), result.fileSize);

      // Trigger change detection to update the UI
      this.cdr.detectChanges();
    }
  }

  /**
   * Get a function to determine if a field should be hidden for a specific table
   */
  getShouldHideFieldFn(tableKey: string): (field: any) => boolean {
    const filesTableKey = this.getFieldKey('files');

    return (field: any) => {
      // Hide file_name field in the Files table (it's displayed in the file upload area)
      if (tableKey === filesTableKey) {
        const fieldKey = this.getFieldKey(field.field_name);
        return fieldKey === 'file_name';
      }
      return false;
    };
  }

  /**
   * Setup page actions based on current mode.
   * Only shown in view mode.
   */
  private setupPageActions(): void {
    // Hide all actions for cancelled sales orders in view mode
    if (this.mode === 'view' && this.isCancelled) {
      this.pageActionService.setActions([]);
      return;
    }

    if (this.mode === 'view') {
      // Disable Edit, Delete, Create SI when SO is closed or billed (same restrictions)
      const isSOLocked = this.soStatus === 'closed' || this.soStatus === 'billed';
      
      this.pageActionService.setActions([
        { id: 'edit', label: 'Edit', icon: 'pi pi-pencil', disabled: isSOLocked, tooltip: isSOLocked ? 'Cannot edit Sales Order - order is closed or billed' : undefined },
        { id: 'duplicate', label: 'Duplicate', icon: 'pi pi-copy' },
        { 
          id: 'create-si', 
          label: 'Create SI', 
          icon: 'pi pi-file-plus',
          disabled: isSOLocked,
          tooltip: isSOLocked ? 'Cannot create SI for closed or billed Sales Order' : undefined
        },
        { id: 'print', label: 'Print', icon: 'pi pi-print', showInToolbar: true },
        { id: 'delete', label: 'Delete', icon: 'pi pi-trash', disabled: isSOLocked, tooltip: isSOLocked ? 'Cannot delete Sales Order - order is closed or billed' : undefined },
      ]);
    } else {
      // Clear actions for create/edit modes
      this.pageActionService.setActions([]);
    }
  }

  /**
   * Handle edit action - opens edit mode in a new tab
   */
  private onEditSO(): void {
    if (!this.clientSOId) {
      this.toasterService.showMessage(
        this.toasterService.MESSAGE_TYPES.ERROR,
        'Sales Order ID is required to edit'
      );
      return;
    }

    // Get base href to support apps deployed with different base paths
    const baseHref = this.getBaseHref();
    
    // Create URL tree with commands
    const urlTree = this.router.createUrlTree(['/clients/sales-order'], {
      queryParams: { soId: this.clientSOId, mode: 'edit' }
    });
    const serializedUrl = this.router.serializeUrl(urlTree);
    
    // Construct the full path with base href
    const pathWithBase = baseHref ? `${baseHref}${serializedUrl}` : serializedUrl;
    
    // Get the full base href URL for constructing absolute URL
    const baseHrefUrl = document.querySelector('base')?.href || window.location.origin;
    const fullUrl = new URL(pathWithBase, baseHrefUrl).href;
    
    // Open in new tab
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  }

  /**
   * Get the base href from the DOM or default to '/'
   * This handles apps deployed with different base hrefs (e.g., /accounting/, /demo/)
   */
  private getBaseHref(): string {
    const baseTag = document.querySelector('base');
    if (baseTag && baseTag.getAttribute('href')) {
      const baseHref = baseTag.getAttribute('href') || '/';
      // Ensure it ends with / and remove trailing slash for path construction
      return baseHref.endsWith('/') ? baseHref.slice(0, -1) : baseHref;
    }
    return '';
  }

  /**
   * Handle duplicate action from page actions popover.
   * Opens a new tab with the sales order in duplicate mode.
   */
  private onDuplicateSO(): void {
    if (!this.clientSOId) {
      this.toasterService.showMessage(
        this.toasterService.MESSAGE_TYPES.ERROR,
        'Sales Order ID is required to duplicate'
      );
      return;
    }

    // Get base href to support apps deployed with different base paths
    const baseHref = this.getBaseHref();
    
    // Create URL tree with commands
    const urlTree = this.router.createUrlTree(['/clients/sales-order'], {
      queryParams: { soId: this.clientSOId, mode: 'duplicate' }
    });
    const serializedUrl = this.router.serializeUrl(urlTree);
    
    // Construct the full path with base href
    const pathWithBase = baseHref ? `${baseHref}${serializedUrl}` : serializedUrl;
    
    // Get the full base href URL for constructing absolute URL
    const baseHrefUrl = document.querySelector('base')?.href || window.location.origin;
    const fullUrl = new URL(pathWithBase, baseHrefUrl).href;
    
    // Open in new tab
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  }

  /**
   * Handle print action - generates and downloads PDF
   */
  private async onPrintSO(): Promise<void> {
    if (!this.clientSOId) {
      this.toasterService.showMessage(
        this.toasterService.MESSAGE_TYPES.ERROR,
        'Sales Order ID is required to print'
      );
      return;
    }

    try {
      this.isLoading = true;
      
      // Fetch print data from backend
      const response = await lastValueFrom(this.clientOrderService.getPrintData(this.clientSOId));
      const printResponse = response?.data || response;
      
      if (!printResponse) {
        throw new Error('Failed to fetch print data');
      }

      // Extract template_id and print_data from response
      const templateId = printResponse.template_id;
      const orderNumber = printResponse.print_data?.order_number || this.clientSOId;
      
      this.pdfApiService.generateAndOpenInNewTab({
        template_id: templateId,
        content: printResponse.print_data,
        filename: `sales_order_${orderNumber}.pdf`
      });

      this.toasterService.showMessage(
        this.toasterService.MESSAGE_TYPES.SUCCESS,
        'PDF opened in new tab for preview'
      );
    } catch (error: any) {
      console.error('Error generating PDF:', error);
      this.toasterService.showMessage(
        this.toasterService.MESSAGE_TYPES.ERROR,
        error?.message || 'Failed to generate PDF'
      );
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Remove all primary key 'id' fields from payload when duplicating an SO
   * This ensures a new SO is created instead of updating the original
   * Preserves all foreign key references (fields ending with _id)
   * Also filters out deleted items and removes is_deleted field from remaining items
   */
  private removeIdsForDuplicate(payload: any): any {
    if (!payload || typeof payload !== 'object') {
      return payload;
    }

    // Handle arrays - filter out deleted items first, then process remaining
    if (Array.isArray(payload)) {
      return payload
        .filter(item => {
          // Filter out items marked as deleted
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            const isDeleted = item.is_deleted === true || item.is_deleted === 1 || item.is_deleted === '1' || item.is_deleted === 'true';
            return !isDeleted;
          }
          return true;
        })
        .map(item => this.removeIdsForDuplicate(item));
    }

    // Handle objects
    const cleaned: any = {};
    for (const key in payload) {
      if (!payload.hasOwnProperty(key)) continue;

      // Skip primary key 'id' field (but preserve foreign keys like client_id, item_id, etc.)
      if (key === 'id') {
        continue;
      }

      // Skip is_deleted field - not needed for new items in duplicate mode
      if (key === 'is_deleted') {
        continue;
      }

      // Recursively process nested objects and arrays
      const value = payload[key];
      if (value && typeof value === 'object') {
        cleaned[key] = this.removeIdsForDuplicate(value);
      } else {
        cleaned[key] = value;
      }
    }

    return cleaned;
  }

  /**
   * Handle create SI action - opens sales invoice creation in a new tab
   */
  private onCreateSI(): void {
    if (!this.clientSOId) {
      this.toasterService.showMessage(
        this.toasterService.MESSAGE_TYPES.ERROR,
        'Sales Order ID is required to create invoice'
      );
      return;
    }

    // Prevent creating SI for closed or billed Sales Orders
    if (this.soStatus === 'closed' || this.soStatus === 'billed') {
      this.toasterService.showMessage(
        this.toasterService.MESSAGE_TYPES.WARNING,
        'Cannot create Sales Invoice for a closed or billed Sales Order'
      );
      return;
    }

    // Get base href to support apps deployed with different base paths
    const baseHref = this.getBaseHref();
    
    // Create URL tree with commands
    const urlTree = this.router.createUrlTree(['/clients/client-invoice'], {
      queryParams: { clientOrderId: this.clientSOId, mode: 'create' }
    });
    const serializedUrl = this.router.serializeUrl(urlTree);
    
    // Construct the full path with base href
    const pathWithBase = baseHref ? `${baseHref}${serializedUrl}` : serializedUrl;
    
    // Get the full base href URL for constructing absolute URL
    const baseHrefUrl = document.querySelector('base')?.href || window.location.origin;
    const fullUrl = new URL(pathWithBase, baseHrefUrl).href;
    
    // Open in new tab
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  }

  /**
   * Handle delete action - deletes the sales order and navigates to list
   */
  private async onDeleteSO(): Promise<void> {
    if (!this.clientSOId) {
      this.toasterService.showMessage(
        this.toasterService.MESSAGE_TYPES.ERROR,
        'Sales Order ID is required to delete'
      );
      return;
    }

    if (!confirm('Are you sure you want to delete this sales order?')) {
      return;
    }

    try {
      this.isLoading = true;
      await lastValueFrom(this.clientOrderService.deleteClientOrder(this.clientSOId));
      this.toasterService.success('Success', 'Sales order deleted successfully');
      this.router.navigate(['/clients/sales-order-list']);
    } catch (error: any) {
      const message = error?.error?.message || error?.message || 'Failed to delete sales order';
      this.toasterService.error('Error', message);
    } finally {
      this.isLoading = false;
    }
  }

}
